import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Tag, ShoppingBag, Layers, Sparkles, DollarSign, Upload,
  Check, Trash2, RefreshCw, EyeOff, Eye, Plus, Users, MousePointerClick, BellRing, Clock, Wand2,
  Mail, FileText, X, ShieldCheck, Zap, Globe, Tags, Gift, AlertTriangle, Image as ImageIcon, MapPin, Ruler, ClipboardCheck, Inbox } from "lucide-react";
import api, { apiError, API } from "../lib/apiClient";
import { categoryImage } from "../lib/categoryImages";
import { Logo } from "../components/Logo";
import { useSite } from "../context/SiteContext";
import { Editable } from "../components/Editable";
import CatalogImport from "./admin/CatalogImport";
import ListingApprovals from "./admin/ListingApprovals";
import SizingResearchImport from "./admin/SizingResearchImport";
import SizingRecovery from "./admin/SizingRecovery";
import CanonicalCharts from "./admin/CanonicalCharts";
import HeldReview from "./admin/HeldReview";
import ContactInbox from "./admin/ContactInbox";
import HelpSupportAdmin, { MerchantOps } from "./admin/HelpSupportAdmin";
import SizingPromotion from "./admin/SizingPromotion";

const TABS = [
  { id: "approvals", label: "Listing Approvals", icon: ClipboardCheck, group: "OVERVIEW" },
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, group: "OVERVIEW" },
  { id: "contactinbox", label: "Contact inbox", icon: Inbox, group: "OVERVIEW" },
  { id: "helpsupport", label: "Help & Support (AI)", icon: Inbox, group: "OVERVIEW" },
  { id: "merchantops", label: "Merchant connections (ops)", icon: Users, group: "PARTNERS & REVENUE" },
  { id: "products", label: "Deal Manager", icon: ShoppingBag, group: "CATALOG" },
  { id: "collections", label: "Collections", icon: Layers, group: "CATALOG" },
  { id: "catalog", label: "Catalog Import", icon: Upload, group: "CATALOG" },
  { id: "categories", label: "Categories", icon: Tags, group: "CATALOG" },
  { id: "canonicalcharts", label: "Canonical Charts (verify · activate)", icon: Ruler, group: "SIZING" },
  { id: "heldreview", label: "Held Conflicts (review)", icon: Ruler, group: "SIZING" },
  { id: "sizingpromotion", label: "Sizing Promotion (preview → live)", icon: Ruler, group: "SIZING" },
  { id: "sizingimport", label: "Sizing Research Import", icon: Upload, group: "SIZING" },
  { id: "sizingrecovery", label: "Sizing Recovery (typed adapter)", icon: Upload, group: "SIZING" },
  { id: "sizecharts", label: "Size Charts (legacy, read-only)", icon: Ruler, group: "LEGACY SIZING (READ-ONLY)" },
  { id: "sizingengine", label: "Sizing Engine (legacy, read-only)", icon: Wand2, group: "LEGACY SIZING (READ-ONLY)" },
  { id: "partners", label: "Partners", icon: Users, group: "PARTNERS & REVENUE" },
  { id: "pricing", label: "Pricing", icon: DollarSign, group: "PARTNERS & REVENUE" },
  { id: "earnings", label: "Earnings", icon: DollarSign, group: "PARTNERS & REVENUE" },
  { id: "ai", label: "AI + Seeds", icon: Sparkles, group: "SYSTEM" },
  { id: "papers", label: "Email Papers (sales division — inactive)", icon: Mail, group: "INACTIVE / FUTURE FEATURES" },
];
const GROUPS = ["OVERVIEW", "CATALOG", "SIZING", "LEGACY SIZING (READ-ONLY)", "PARTNERS & REVENUE", "SYSTEM", "INACTIVE / FUTURE FEATURES"];

const DAYS = [
  { v: 6, label: "Sunday" }, { v: 0, label: "Monday" }, { v: 1, label: "Tuesday" },
  { v: 2, label: "Wednesday" }, { v: 3, label: "Thursday" }, { v: 4, label: "Friday" }, { v: 5, label: "Saturday" },
];
const dayLabel = (v) => (DAYS.find((d) => d.v === v) || {}).label || "weekly";
const HOURS = Array.from({ length: 24 }, (_, v) => {
  const h12 = v % 12 === 0 ? 12 : v % 12;
  return { v, label: `${h12} ${v < 12 ? "AM" : "PM"}` };
});
const hourLabel = (v) => (HOURS.find((h) => h.v === v) || {}).label || "6 AM";

export default function Admin() {
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return TABS.some((x) => x.id === t) ? t : "approvals";
  });
  const [features, setFeatures] = useState({ sales_division_enabled: false });
  useEffect(() => { api.get("/features").then(({ data }) => setFeatures(data)).catch(() => {}); }, []);
  const visibleTabs = TABS.filter((t) => t.group !== "INACTIVE / FUTURE FEATURES" || features.sales_division_enabled || tab === t.id || true);
  return (
    <div className="fade-up">
      <div className="flex items-center gap-3 mb-6">
        <Logo height="h-8" />
        <span className="px-2.5 py-1 rounded-full bg-ink text-white text-xs font-bold">Admin</span>
      </div>
      <nav aria-label="Admin sections" className="mb-6 space-y-2" data-testid="admin-nav">
        {GROUPS.map((g) => {
          const items = visibleTabs.filter((t) => t.group === g);
          if (!items.length) return null;
          const inactive = g === "INACTIVE / FUTURE FEATURES";
          return (
            <div key={g} className={`flex flex-wrap items-center gap-2 ${inactive ? "opacity-70" : ""}`} data-testid={`admin-nav-group-${g.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>
              <span className="text-[11px] font-black tracking-wider text-ink-muted w-28 shrink-0">{g}{inactive && !features.sales_division_enabled ? " (flag off)" : ""}</span>
              {items.map((t) => (
                <button key={t.id} onClick={() => { setTab(t.id); window.history.replaceState(null, "", `/admin?tab=${t.id}`); }} data-testid={`admin-tab-${t.id}`} aria-label={t.label} aria-current={tab === t.id ? "page" : undefined}
                  className={`px-4 py-2 rounded-full font-semibold text-sm whitespace-nowrap flex items-center gap-1.5 transition-all ${tab === t.id ? "bg-brand text-white shadow-md" : "bg-white border border-line text-ink-soft hover:border-brand/30"}`}>
                  <t.icon size={16} aria-hidden="true" /> {t.label}
                </button>
              ))}
            </div>
          );
        })}
      </nav>
      {tab === "approvals" && <ListingApprovals onOpenDeal={() => setTab("products")} />}
      {tab === "sizingimport" && <SizingResearchImport />}
      {tab === "sizingrecovery" && <SizingRecovery />}
      {tab === "canonicalcharts" && <CanonicalCharts />}
      {tab === "heldreview" && <HeldReview />}
      {tab === "contactinbox" && <ContactInbox />}
      {tab === "helpsupport" && <HelpSupportAdmin />}
      {tab === "merchantops" && <MerchantOps />}
      {tab === "sizingpromotion" && <SizingPromotion />}
      {tab === "dashboard" && <Dashboard />}
      {tab === "papers" && <EmailPapers />}
      {tab === "products" && <ProductManager />}
      {tab === "collections" && <CollectionManager />}
      {tab === "catalog" && <CatalogImport />}
      {(tab === "sizecharts" || tab === "sizingengine") && (
        <div data-testid="legacy-sizing-readonly">
          <div className="mb-3 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="legacy-sizing-banner"><b>Legacy Sizing — read-only.</b> This workbook data (533 rows / 21 brands) is preserved as history and evidence only. It is NOT used for customer recommendations, brand selectors or coverage counts; those read canonical recommendation-eligible charts exclusively (Canonical Charts tab). Editing and import controls are disabled; legacy writes are frozen server-side (LEGACY_SIZING_WRITES=off).</div>
          <fieldset disabled className="legacy-readonly" data-testid="legacy-sizing-fieldset">{tab === "sizecharts" ? <SizeCharts /> : <SizingEngine />}</fieldset>
        </div>
      )}
      {tab === "categories" && <CategoriesManager />}
      {tab === "ai" && <AITools />}
      {tab === "pricing" && <PricingManager />}
      {tab === "partners" && <PartnersManager />}
      {tab === "earnings" && <EarningsReport />}
      <AdminStyles />
    </div>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then(({ data }) => setStats(data)).catch(() => {}); }, []);
  if (!stats) return <div className="text-ink-muted">Loading…</div>;
  const cards = [
    { label: "Registered families", value: stats.families, icon: Users },
    { label: "Total users", value: stats.new_users, icon: Users },
    { label: "Paid members", value: stats.paid_members, icon: DollarSign },
    { label: "Active alerts", value: stats.active_alerts, icon: BellRing },
    { label: "Awaiting approval", value: stats.awaiting_approval, icon: Clock, href: stats.awaiting_link, hint: `${stats.awaiting_listings} listings in the approval queue${stats.awaiting_sales ? ` + ${stats.awaiting_sales} sales` : ""}` },
    { label: "Link clicks", value: stats.link_clicks, icon: MousePointerClick },
  ];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {cards.map((c) => (
          <div key={c.label} data-testid={`stat-${c.label.replace(/\s+/g, "-").toLowerCase()}`} className={`bg-white rounded-2xl border border-line shadow-soft p-5 ${c.href ? "cursor-pointer hover:border-brand/40" : ""}`}
            role={c.href ? "link" : undefined} tabIndex={c.href ? 0 : undefined} onClick={() => c.href && (window.location.href = c.href)} onKeyDown={(e) => c.href && e.key === "Enter" && (window.location.href = c.href)}>
            <c.icon className="text-brand mb-3" size={22} aria-hidden="true" />
            <p className="font-heading font-black text-3xl text-ink">{c.value}</p>
            <p className="text-sm text-ink-muted font-semibold">{c.label}</p>
            {c.hint && <p className="text-xs text-brand font-semibold mt-1" data-testid="stat-awaiting-hint">{c.hint} →</p>}
          </div>
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-line shadow-soft p-5">
        <h3 className="font-heading font-bold text-lg text-ink mb-3">Top retailers by clicks</h3>
        {stats.top_retailers.length === 0 ? <p className="text-ink-muted text-sm">No clicks yet.</p> : (
          <div className="space-y-2">{stats.top_retailers.map((r) => (
            <div key={r.name} className="flex justify-between text-sm"><span className="text-ink-soft font-semibold">{r.name}</span><span className="font-bold text-ink">{r.clicks}</span></div>
          ))}</div>
        )}
      </div>
    </div>
  );
}

const FILTER_LABEL = { batch: "Import batch", status: "Status", retailer: "Retailer", brand: "Brand", occasion: "Occasion", department: "Department", gender: "Gender", clothing_type: "Clothing type", image_status: "Image status", chart_status: "Size-chart status" };
const STATUS_LABEL = { needs_review: "Needs Review", blocked: "Blocked", approved: "Approved", rejected: "Rejected", approved_match: "Approved chart match", no_match: "No approved chart",
  current: "Corrected Parent Products / Current Import", legacy: "Legacy Failed Import", imports: "All Imports", manual: "Manual (non-import) products", all: "All products" };
const STATE_CLS = { approved: "bg-emerald-100 text-emerald-800", needs_review: "bg-amber-100 text-amber-800", blocked: "bg-red-100 text-red-800", rejected: "bg-slate-200 text-slate-800" };
const DEFAULT_FILTERS = { batch: "current" };

function DealFilters({ filters, facets, counts, batchSummary, onChange }) {
  const keys = ["batch", "status", "retailer", "brand", "occasion", "department", "gender", "clothing_type", "image_status", "chart_status"];
  const bs = batchSummary || {};
  return (
    <div className="bg-white rounded-2xl border border-line shadow-soft p-4 space-y-3" data-testid="deal-filters">
      <div className="flex flex-wrap gap-2 text-xs" data-testid="deal-batch-summary">
        <span className="px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-900 font-bold" data-testid="deal-batch-current">Current import: {bs.current?.records ?? 0} records · {bs.current?.import_ready ?? 0} import-ready{bs.current?.imported_at ? ` · ${bs.current.imported_at}` : ""}</span>
        <span className="px-2.5 py-1 rounded-full bg-slate-100 border border-slate-300 text-slate-800 font-bold" data-testid="deal-batch-legacy">Legacy failed import: {bs.legacy_failed?.records ?? 0} records · all blocked · DO NOT REVIEW</span>
        <span className="px-2.5 py-1 rounded-full bg-cream border border-line text-ink-soft font-bold" data-testid="deal-batch-manual">Manual products: {bs.manual?.records ?? 0}</span>
      </div>
      <div className="flex flex-wrap gap-2 text-xs font-bold" data-testid="deal-counts">
        {["needs_review", "blocked", "approved", "rejected"].map((s) => (
          <button key={s} data-testid={`deal-count-${s}`} onClick={() => onChange({ status: filters.status === s ? "" : s })} className={`px-2.5 py-1 rounded-full border transition-all ${STATE_CLS[s]} ${filters.status === s ? "ring-2 ring-brand" : "border-transparent"}`}>{STATUS_LABEL[s]}: {counts[s] ?? 0}</button>
        ))}
        <span className="px-2.5 py-1 rounded-full bg-cream text-ink-soft" data-testid="deal-count-all">Showing: {counts.all ?? 0} · {STATUS_LABEL[filters.batch || "all"]}</span>
      </div>
      <div className="grid sm:grid-cols-3 lg:grid-cols-4 gap-2">
        <input data-testid="deal-filter-q" className="ainput h-10 text-sm" placeholder="Search title / SKU / brand" value={filters.q || ""} onChange={(e) => onChange({ q: e.target.value })} />
        {keys.map((k) => (
          <select key={k} data-testid={`deal-filter-${k}`} className={`ainput h-10 text-sm ${k === "batch" ? "border-brand font-bold" : ""}`} value={filters[k] || ""} onChange={(e) => onChange({ [k]: e.target.value })}>
            {k !== "batch" && <option value="">{FILTER_LABEL[k] + ": all"}</option>}
            {(facets[k] || []).map((v) => { const label = k === "batch" ? FILTER_LABEL.batch + ": " + STATUS_LABEL[v] : (STATUS_LABEL[v] || v); return <option key={v} value={v}>{label}</option>; })}
          </select>
        ))}
        <button data-testid="deal-filter-clear" onClick={() => onChange(null)} className="btn-ghost h-10 text-xs justify-center">Reset to current import</button>
      </div>
    </div>
  );
}

function ProductManager() {
  const { optionsFor } = useSite();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], counts: {}, facets: {}, batch_summary: {} });
  const [filters, setFilters] = useState(() => {
    const fromUrl = Object.fromEntries(new URLSearchParams(window.location.search).entries());
    delete fromUrl.tab;
    return Object.keys(fromUrl).length ? fromUrl : { ...DEFAULT_FILTERS };
  });
  const [form, setForm] = useState({ name: "", gender: "boy", category: "set", price: 0, sizes: "", image: "", retailer: "", affiliate_url: "", occasion: "everyday" });
  const qs = new URLSearchParams(Object.entries(filters).filter(([k, v]) => v && k !== "tab")).toString();
  const load = useCallback(() => api.get(`/admin/review/products${qs ? `?${qs}` : ""}`).then(({ data }) => setData(data)).catch(() => {}), [qs]);
  useEffect(() => { load(); }, [load]);
  const onChange = (patch) => setFilters((f) => (patch === null ? { ...DEFAULT_FILTERS } : { ...f, ...patch }));
  const create = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    try { await api.post("/admin/products", { ...form, price: Number(form.price), sizes: form.sizes.split(",").map((s) => s.trim()).filter(Boolean) }); toast.success("Product added as hidden draft"); setForm({ name: "", gender: "boy", category: "set", price: 0, sizes: "", image: "", retailer: "", affiliate_url: "", occasion: "everyday" }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggleHide = async (p) => {
    try { await api.put(`/admin/products/${p.id}`, { hidden: !p.hidden }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail?.message || e.response?.data?.detail)); }
  };
  const del = async (p) => { if (!window.confirm(`Delete "${p.name}" permanently? This cannot be undone.`)) return; await api.delete(`/admin/products/${p.id}`); toast.success("Deleted"); load(); };
  const review = (id) => navigate(`/admin/review/${id}${qs ? `?${qs}` : ""}`);
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-line shadow-soft p-5 space-y-3">
        <Editable id="admin.products.addtitle" as="h3" className="font-heading font-bold text-lg text-ink">Add Clothing Deal (link a product)</Editable>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <input data-testid="prod-name-input" className="ainput" placeholder="Product name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <select data-testid="prod-gender-select" className="ainput" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>{optionsFor("product_gender", ["boy", "girl", "unisex"]).map((g) => <option key={g}>{g}</option>)}</select>
          <select data-testid="prod-occasion-select" className="ainput" value={form.occasion} onChange={(e) => setForm({ ...form, occasion: e.target.value })}>{optionsFor("occasion", ["everyday", "shabbos", "yom_tov", "wedding", "school", "pajamas"]).map((o) => <option key={o}>{o}</option>)}</select>
          <input data-testid="prod-price-input" className="ainput" type="number" placeholder="Price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
          <input data-testid="prod-sizes-input" className="ainput" placeholder="Sizes (comma sep)" value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} />
          <input data-testid="prod-image-input" className="ainput" placeholder="Image URL" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} />
          <input data-testid="prod-retailer-input" className="ainput" placeholder="Retailer" value={form.retailer} onChange={(e) => setForm({ ...form, retailer: e.target.value })} />
          <input data-testid="prod-url-input" className="ainput" placeholder="Affiliate URL" value={form.affiliate_url} onChange={(e) => setForm({ ...form, affiliate_url: e.target.value })} />
        </div>
        <button data-testid="prod-create-btn" onClick={create} className="btn-primary"><Plus size={16} /> Add Product</button>
      </div>
      <DealFilters filters={filters} facets={data.facets} counts={data.counts} batchSummary={data.batch_summary} onChange={onChange} />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="deal-grid">
        {data.items.map((p) => (
          <div key={p.id} data-testid={`admin-product-${p.id}`} className={`bg-white rounded-2xl border shadow-soft overflow-hidden ${p.batch === "legacy_failed" ? "border-slate-400 opacity-70" : "border-line"} ${p.hidden ? "" : ""}`}>
            {p.batch === "legacy_failed" && <div className="bg-slate-700 text-white text-[11px] font-black tracking-wider text-center py-1" data-testid={`prod-legacy-${p.id}`}>LEGACY FAILED IMPORT — DO NOT REVIEW</div>}
            <button onClick={() => review(p.id)} className="w-full h-40 bg-[#F6F2EE] flex items-center justify-center" data-testid={`prod-image-${p.id}`}>
              {p.image ? <img src={p.image} alt={p.name} className="max-h-full max-w-full object-contain" loading="lazy" /> : <span className="text-xs text-ink-muted">No image</span>}
            </button>
            <div className="p-3">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span data-testid={`prod-state-${p.id}`} className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${STATE_CLS[p.review_state]}`}>{STATUS_LABEL[p.review_state]}</span>
                <span data-testid={`prod-occasion-${p.id}`} className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${p.occasion_valid ? "bg-brand-light text-brand" : "bg-red-100 text-red-800"}`}>{p.occasion_valid ? p.occasion_canon : `occasion: ${p.occasion || "missing"} ✕`}</span>
                <span className="text-[11px] text-ink-muted ml-auto">{p.batch === "current" ? "current import" : p.batch === "legacy_failed" ? "legacy" : "manual"} · {p.hidden ? "hidden" : "visible"}{p.block_count ? ` · ${p.block_count} blocks` : ""}</span>
              </div>
              <p className="font-bold text-ink text-sm leading-snug">{p.name || "(untitled)"}</p>
              <p className="text-xs text-ink-muted">{p.retailer}{p.brand ? ` · ${p.brand}` : ""} · {p.price != null ? `$${p.price}` : "no price"} · {p.gender || "—"}{p.sku ? ` · ${p.sku}` : ""}</p>
              <div className="flex items-center gap-1 mt-2">
                <button data-testid={`prod-review-${p.id}`} onClick={() => review(p.id)} className="btn-primary h-8 px-3 text-xs"><Eye size={14} /> Review</button>
                <button data-testid={`prod-hide-${p.id}`} onClick={() => toggleHide(p)} title={p.hidden ? "Attempt to unhide (gate-checked)" : "Hide"} className="iconbtn text-ink-muted ml-auto">{p.hidden ? <Globe size={15} /> : <EyeOff size={15} />}</button>
                <button data-testid={`prod-delete-${p.id}`} onClick={() => del(p)} className="iconbtn text-brand"><Trash2 size={15} /></button>
              </div>
            </div>
          </div>
        ))}
        {data.items.length === 0 && <p className="text-sm text-ink-muted" data-testid="deal-grid-empty">No products match these filters.</p>}
      </div>
      <AdminStyles />
    </div>
  );
}

function CollectionManager() {
  const { optionsFor } = useSite();
  const [collections, setCollections] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ name: "", whom: "whole_family", occasion: "shabbos", match_type: "coordinating", image: "", item_ids: [] });
  const load = useCallback(() => {
    api.get("/admin/collections").then(({ data }) => setCollections(data)).catch(() => {});
    api.get("/admin/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const toggleItem = (id) => setForm((f) => ({ ...f, item_ids: f.item_ids.includes(id) ? f.item_ids.filter((x) => x !== id) : [...f.item_ids, id] }));
  const create = async () => {
    if (!form.name || form.item_ids.length === 0) { toast.error("Name and at least one product required"); return; }
    const img = form.image || (products.find((p) => p.id === form.item_ids[0]) || {}).image;
    try { await api.post("/admin/collections", { ...form, image: img }); toast.success("Collection built"); setForm({ name: "", whom: "whole_family", occasion: "shabbos", match_type: "coordinating", image: "", item_ids: [] }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/admin/collections/${id}`); toast.success("Deleted"); load(); };
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-line shadow-soft p-5 space-y-3">
        <Editable id="admin.collections.title" as="h3" className="font-heading font-bold text-lg text-ink">Collection Builder</Editable>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <input data-testid="coll-name-input" className="ainput" placeholder="Collection name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <select data-testid="coll-whom-select" className="ainput" value={form.whom} onChange={(e) => setForm({ ...form, whom: e.target.value })}>{optionsFor("collection_whom", ["whole_family", "boys", "girls", "sibling"]).map((w) => <option key={w}>{w}</option>)}</select>
          <select data-testid="coll-occasion-select" className="ainput" value={form.occasion} onChange={(e) => setForm({ ...form, occasion: e.target.value })}>{optionsFor("occasion", ["everyday", "shabbos", "yom_tov", "wedding", "school", "pajamas"]).map((o) => <option key={o}>{o}</option>)}</select>
          <select data-testid="coll-match-select" className="ainput" value={form.match_type} onChange={(e) => setForm({ ...form, match_type: e.target.value })}>{optionsFor("match_type", ["coordinating", "exact", "same_color", "same_brand"]).map((m) => <option key={m}>{m}</option>)}</select>
        </div>
        <p className="text-xs font-bold uppercase tracking-wider text-ink-muted">Select products ({form.item_ids.length})</p>
        <div className="flex flex-wrap gap-2 max-h-48 overflow-auto">
          {products.map((p) => (
            <button key={p.id} data-testid={`coll-pick-${p.id}`} onClick={() => toggleItem(p.id)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${form.item_ids.includes(p.id) ? "bg-brand text-white border-brand" : "bg-cream border-line text-ink-soft"}`}>{p.name}</button>
          ))}
        </div>
        <button data-testid="coll-create-btn" onClick={create} className="btn-primary"><Plus size={16} /> Build Collection</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {collections.map((c) => (
          <div key={c.id} data-testid={`admin-collection-${c.id}`} className="bg-white rounded-2xl border border-line shadow-soft overflow-hidden">
            <img src={c.image} alt={c.name} className="w-full h-32 object-cover" />
            <div className="p-3 flex items-center justify-between">
              <div><p className="font-bold text-ink text-sm">{c.name}</p><p className="text-xs text-ink-muted">{c.occasion} · {c.item_ids.length} items</p></div>
              <button data-testid={`coll-delete-${c.id}`} onClick={() => del(c.id)} className="iconbtn text-brand"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
      <AdminStyles />
    </div>
  );
}

function AITools() {
  const { optionsFor } = useSite();
  const [seeds, setSeeds] = useState([]);
  const [form, setForm] = useState({ title: "", gender: "girl", category: "dress", price: 0, image: "" });
  const [ranked, setRanked] = useState(null);
  const [running, setRunning] = useState(false);
  const load = useCallback(() => api.get("/admin/seeds").then(({ data }) => setSeeds(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!form.title) { toast.error("Title required"); return; }
    await api.post("/admin/seeds", { ...form, price: Number(form.price) }); toast.success("Seed added"); setForm({ title: "", gender: "girl", category: "dress", price: 0, image: "" }); load();
  };
  const del = async (id) => { await api.delete(`/admin/seeds/${id}`); load(); };
  const runMatch = async (seedId) => {
    setRunning(true); setRanked(null);
    toast.loading("AI matching against your standard…", { id: "match" });
    try {
      const { data } = await api.post("/admin/ai/match", { seed_id: seedId });
      setRanked(data.ranked); toast.success(`AI ranked ${data.ranked.length} products`, { id: "match" });
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Match failed", { id: "match" }); }
    finally { setRunning(false); }
  };
  return (
    <div className="space-y-6">
      <div className="bg-brand-light border border-brand/20 rounded-2xl p-4 text-sm text-ink-soft flex items-center gap-2"><Sparkles className="text-brand" size={18} /> AI is admin-assisted: seed your verified benchmark deals, then let AI find & rank similar products. Nothing publishes without your approval.</div>
      <div className="bg-white rounded-2xl border border-line shadow-soft p-5 space-y-3">
        <Editable id="admin.ai.seedtitle" as="h3" className="font-heading font-bold text-lg text-ink">Seed-Deal Library (train the AI)</Editable>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <input data-testid="seed-title-input" className="ainput" placeholder="Benchmark title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <select data-testid="seed-gender-select" className="ainput" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>{optionsFor("product_gender", ["girl", "boy", "unisex"]).map((g) => <option key={g}>{g}</option>)}</select>
          <input data-testid="seed-price-input" className="ainput" type="number" placeholder="Target price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
          <input data-testid="seed-image-input" className="ainput" placeholder="Image URL" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} />
        </div>
        <button data-testid="seed-create-btn" onClick={create} className="btn-primary"><Plus size={16} /> Add Seed</button>
      </div>
      <div className="space-y-3">
        {seeds.map((s) => (
          <div key={s.id} data-testid={`seed-${s.id}`} className="bg-white rounded-2xl border border-line shadow-soft p-4 flex items-center justify-between gap-3">
            <div><p className="font-bold text-ink">{s.title}</p><p className="text-xs text-ink-muted">{s.gender} · {s.category} · ${s.price}</p></div>
            <div className="flex gap-2">
              <button data-testid={`seed-match-${s.id}`} disabled={running} onClick={() => runMatch(s.id)} className="btn-ghost disabled:opacity-50"><Wand2 size={15} /> Run AI match</button>
              <button data-testid={`seed-delete-${s.id}`} onClick={() => del(s.id)} className="iconbtn text-brand"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
      {ranked && (
        <div className="bg-white rounded-2xl border border-line shadow-soft p-5">
          <h3 className="font-heading font-bold text-lg text-ink mb-3">AI ranked results (awaiting your approval)</h3>
          <div className="space-y-2">{ranked.map((r) => (
            <div key={r.id} data-testid={`ranked-${r.id}`} className="flex items-center gap-3 p-2 rounded-xl border border-line">
              {r.product?.image && <img src={r.product.image} alt="" className="w-12 h-12 rounded-lg object-cover" />}
              <div className="flex-1"><p className="font-semibold text-ink text-sm">{r.product?.name}</p><p className="text-xs text-ink-muted">{r.reason}</p></div>
              <span className="font-heading font-black text-brand text-lg">{r.score}</span>
            </div>
          ))}</div>
        </div>
      )}
      <AdminStyles />
    </div>
  );
}

function PricingManager() {
  const [pricing, setPricing] = useState({ monthly_price: 4.99, yearly_price: 39 });
  useEffect(() => { api.get("/pricing").then(({ data }) => setPricing(data)).catch(() => {}); }, []);
  const save = async () => {
    try { await api.put("/admin/pricing", { monthly_price: Number(pricing.monthly_price), yearly_price: Number(pricing.yearly_price) }); toast.success("Pricing updated"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="max-w-md space-y-4">
      <div className="bg-white rounded-2xl border border-line shadow-soft p-5 space-y-4">
        <Editable id="admin.pricing.title" as="h3" className="font-heading font-bold text-lg text-ink">Membership Pricing (editable)</Editable>
        <div><label className="text-sm font-bold text-ink">Monthly price ($)</label><input data-testid="pricing-monthly-input" type="number" step="0.01" className="ainput mt-1" value={pricing.monthly_price} onChange={(e) => setPricing({ ...pricing, monthly_price: e.target.value })} /></div>
        <div><label className="text-sm font-bold text-ink">Yearly price ($)</label><input data-testid="pricing-yearly-input" type="number" step="0.01" className="ainput mt-1" value={pricing.yearly_price} onChange={(e) => setPricing({ ...pricing, yearly_price: e.target.value })} /></div>
        <button data-testid="pricing-save-btn" onClick={save} className="btn-primary"><Check size={16} /> Save Pricing</button>
        <p className="text-xs text-ink-muted">Note: display price updates instantly. Stripe charge uses the configured plan.</p>
      </div>
      <AdminStyles />
    </div>
  );
}

function EmailPapers() {
  const [papers, setPapers] = useState([]);
  const [autopost, setAutopost] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [active, setActive] = useState(null); // paper being reviewed
  const [sales, setSales] = useState([]);
  const [rejectReasons, setRejectReasons] = useState({}); // { saleId: reason }
  const [learnVer, setLearnVer] = useState(0);
  const [sources, setSources] = useState([]);
  const [webForm, setWebForm] = useState({ url: "", label: "", instructions: "", trusted: false, auto_scan: false, scan_weekday: 6, scan_hour: 6 });
  const [scanning, setScanning] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(() => {
    api.get("/admin/papers").then(({ data }) => setPapers(data)).catch(() => {});
    api.get("/admin/papers/settings").then(({ data }) => setAutopost(data.autopost)).catch(() => {});
    api.get("/admin/web-sources").then(({ data }) => setSources(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const pollPaper = useCallback((pid) => {
    if (pollRef.current) clearInterval(pollRef.current);
    let ticks = 0;
    const inProgress = (s) => s === "scanning" || s === "processing";
    pollRef.current = setInterval(async () => {
      ticks += 1;
      try {
        const { data: ps } = await api.get("/admin/papers");
        setPapers(ps);
        const p = ps.find((x) => x.id === pid);
        const { data: sd } = await api.get(`/admin/papers/${pid}/sales`);
        setSales(sd);
        if ((p && !inProgress(p.status)) || ticks > 40) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (p && p.status === "done") toast.success(`Finished reading — ${p.sale_count} sales found${p.skipped ? `, ${p.skipped} duplicate(s) skipped` : ""}`, { id: "paper" });
          else if (p && p.status === "error") toast.error("Couldn't read that paper — please try again", { id: "paper" });
        }
      } catch (err) { if (process.env.NODE_ENV === "development") console.debug("paper poll tick failed, will retry", err); }
    }, 6000);
  }, []);

  const setMode = async (val) => {
    try { const { data } = await api.put("/admin/papers/settings", { autopost: val }); setAutopost(data.autopost); toast.success(val ? "Auto-post is ON — new paper sales go live instantly" : "Review-first is ON — you approve every sale"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const upload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    e.target.value = "";
    setUploading(true);
    toast.loading("Uploading paper…", { id: "paper" });
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/papers/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.info(data.message || "Reading the paper in the background — sales will appear shortly.", { id: "paper", duration: 8000 });
      load();
      if (data.paper?.id) { setActive(data.paper.id); pollPaper(data.paper.id); }
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Upload failed", { id: "paper" }); }
    finally { setUploading(false); }
  };

  const openPaper = async (pid) => {
    setActive(pid);
    const { data } = await api.get(`/admin/papers/${pid}/sales`);
    setSales(data);
  };

  const scan = async ({ url, label, source_id }) => {
    setScanning(true);
    toast.loading("AI is reading the publication…", { id: "scan" });
    try {
      const { data } = await api.post("/admin/web-sources/scan", { url, label, source_id });
      if (data.background) { toast.info(data.message, { id: "scan", duration: 8000 }); load(); if (data.paper?.id) { setActive(data.paper.id); pollPaper(data.paper.id); } }
      else if (data.sales.length === 0) toast.info(data.message || "No sales found on that page", { id: "scan" });
      else { toast.success(`Read ${data.sales.length} sales${data.autopost ? " (posted live)" : " — awaiting approval"}`, { id: "scan" }); }
      if (!data.background) { setWebForm({ url: "", label: "" }); load(); if (data.paper?.id) openPaper(data.paper.id); }
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Scan failed", { id: "scan" }); }
    finally { setScanning(false); }
  };

  const addAndScan = async () => {
    if (!webForm.url.trim()) { toast.error("Paste a website or magazine link first"); return; }
    let sid = null;
    try { const { data } = await api.post("/admin/web-sources", webForm); sid = data.id; } catch (err) { if (process.env.NODE_ENV === "development") console.debug("web-source create skipped, scanning ad-hoc", err); }
    setScanning(true);
    toast.loading("AI is reading the publication…", { id: "scan" });
    try {
      const { data } = await api.post("/admin/web-sources/scan", sid ? { source_id: sid } : { url: webForm.url, instructions: webForm.instructions });
      if (data.background) {
        toast.info(data.message, { id: "scan", duration: 8000 });
        setWebForm({ url: "", label: "", instructions: "", trusted: false, auto_scan: false, scan_weekday: 6, scan_hour: 6 });
        load();
        if (data.paper?.id) { setActive(data.paper.id); pollPaper(data.paper.id); }
      } else {
        if (data.sales.length === 0) toast.info(data.message || "No new sales found", { id: "scan" });
        else toast.success(`Read ${data.sales.length} sales${data.skipped ? `, skipped ${data.skipped} duplicates` : ""}${data.autopost ? " (posted live)" : " — awaiting approval"}`, { id: "scan" });
        setWebForm({ url: "", label: "", instructions: "", trusted: false, auto_scan: false, scan_weekday: 6, scan_hour: 6 });
        load();
        if (data.paper?.id) openPaper(data.paper.id);
      }
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Scan failed", { id: "scan" }); }
    finally { setScanning(false); }
  };

  const toggleSource = async (id, field, val) => {
    await api.put(`/admin/web-sources/${id}`, { [field]: val });
    toast.success("Source updated");
    load();
  };

  const delSource = async (id) => { await api.delete(`/admin/web-sources/${id}`); toast.success("Source removed"); load(); };

  const expireNow = async () => {
    const { data } = await api.post("/admin/sales/expire-now");
    toast.success(`Deleted ${data.expired} expired sale${data.expired === 1 ? "" : "s"}`);
  };

  const clearQueue = async () => {
    if (!confirmClear) { setConfirmClear(true); setTimeout(() => setConfirmClear(false), 4000); return; }
    setConfirmClear(false);
    setClearing(true);
    try {
      const { data } = await api.post("/admin/sales/clear-queue");
      toast.success(`Cleared ${data.deleted} pending draft${data.deleted === 1 ? "" : "s"} from the queue`);
      if (active) { setActive(null); setSales([]); }
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not clear queue"); }
    finally { setClearing(false); }
  };

  const viewPdf = (pid, page) => {
    const token = localStorage.getItem("uptoo_token");
    // Open the real endpoint (not a blob) so the browser PDF viewer honours #page=N.
    // NOTE: do NOT append &zoom — Chrome/PDFium resets to page 1 when zoom lacks the
    // full "zoom=scale,left,top" form, which breaks the page jump.
    const frag = page ? `#page=${page}` : "";
    const url = `${API}/admin/papers/${pid}/pdf?token=${encodeURIComponent(token || "")}${frag}`;
    window.open(url, "_blank", "noopener");
  };

  // Open the ORIGINAL for a sale card at its page: a PDF paper opens the PDF zoomed to that
  // page, a web/flipbook paper opens the online issue at that page (issue_url/<page>/).
  const openOriginal = (page) => {
    const ap = papers.find((p) => p.id === active) || {};
    if (ap.gridfs_id) return viewPdf(active, page);
    if (ap.source_url) {
      const base = ap.source_url.replace(/\/+$/, "");
      window.open(page ? `${base}/${page}/` : base, "_blank", "noopener");
    }
  };

  const approve = async (id) => { await api.put(`/admin/sales/${id}`, { status: "approved" }); toast.success("Approved — now live"); openPaper(active); load(); };
  const reject = async (id) => {
    const reason = (rejectReasons[id] || "").trim();
    await api.post(`/admin/sales/${id}/reject`, { reason });
    toast.success(reason ? "Rejected — I'll use that to improve future scans" : "Rejected");
    setRejectReasons((r) => { const n = { ...r }; delete n[id]; return n; });
    setLearnVer((v) => v + 1);
    openPaper(active); load();
  };
  const delPaper = async (pid) => { await api.delete(`/admin/papers/${pid}`); toast.success("Paper & its drafts removed"); if (active === pid) { setActive(null); setSales([]); } load(); };

  return (
    <div className="space-y-6">
      {/* Mode toggle: two separate buttons */}
      <div className="bg-white rounded-2xl border border-line shadow-soft p-5">
        <Editable id="admin.papers.modetitle" as="h3" className="font-heading font-bold text-lg text-ink mb-1">How should paper sales be published?</Editable>
        <p className="text-sm text-ink-muted mb-4">Start in Review-first. Once you trust the AI, switch to Auto-post.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <button
            onClick={() => setMode(false)}
            data-testid="papers-mode-review"
            className={`text-left rounded-2xl border-2 p-4 transition-all ${!autopost ? "border-brand bg-brand-light" : "border-line bg-white hover:border-brand/30"}`}
          >
            <p className="font-bold text-ink flex items-center gap-2"><ShieldCheck size={18} className={!autopost ? "text-brand" : "text-ink-muted"} /> Review first {!autopost && <span className="text-xs text-brand">(active)</span>}</p>
            <p className="text-sm text-ink-soft mt-1">Every extracted sale waits for your approval before going live.</p>
          </button>
          <button
            onClick={() => setMode(true)}
            data-testid="papers-mode-autopost"
            className={`text-left rounded-2xl border-2 p-4 transition-all ${autopost ? "border-gold bg-gold/10" : "border-line bg-white hover:border-gold/40"}`}
          >
            <p className="font-bold text-ink flex items-center gap-2"><Zap size={18} className={autopost ? "text-gold-text" : "text-ink-muted"} /> Auto-post live {autopost && <span className="text-xs text-gold-text">(active)</span>}</p>
            <p className="text-sm text-ink-soft mt-1">Trust the AI — new sales from papers publish instantly, no review.</p>
          </button>
        </div>
      </div>

      {/* Upload */}
      <div className="bg-white rounded-2xl border border-line shadow-soft p-5">
        <Editable id="admin.papers.uploadtitle" as="h3" className="font-heading font-bold text-lg text-ink mb-2">Upload today's paper (PDF)</Editable>
        <p className="text-sm text-ink-muted mb-3">Drop the daily paper you received by email. The AI reads every page and drafts a card per sale.</p>
        <label data-testid="paper-upload-label" className={`btn-primary cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
          <Upload size={16} /> {uploading ? "Reading paper…" : "Upload PDF paper"}
          <input data-testid="paper-upload-input" type="file" accept="application/pdf,image/*" className="hidden" onChange={upload} />
        </label>
      </div>

      {/* Gmail connection */}
      <GmailConnect onIngested={load} />

      {/* AI learning report */}
      <LearningReport version={learnVer} />

      {/* Web publications */}
      <div className="bg-white rounded-2xl border border-line shadow-soft p-5">
        <Editable id="admin.papers.webtitle" as="h3" className="font-heading font-bold text-lg text-ink mb-2">Add a website or magazine link</Editable>
        <p className="text-sm text-ink-muted mb-3">For bigger weekly publications online, paste the link (a webpage or a direct PDF/flyer). The AI reads it and drafts the sales into your review queue.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <input data-testid="web-url-input" className="ainput" placeholder="https://example-magazine.com/this-week" value={webForm.url} onChange={(e) => setWebForm({ ...webForm, url: e.target.value })} />
          <input data-testid="web-label-input" className="ainput" placeholder="Name (optional)" value={webForm.label} onChange={(e) => setWebForm({ ...webForm, label: e.target.value })} />
        </div>
        <textarea
          data-testid="web-instructions-input"
          className="ainput mt-3 !h-auto py-2"
          rows={2}
          placeholder="Notes for the AI about this link — e.g. 'This page has a search bar; look up keywords like sale, clearance, % off. Only clothing & Judaica matter.'"
          value={webForm.instructions}
          onChange={(e) => setWebForm({ ...webForm, instructions: e.target.value })}
        />
        <div className="flex flex-wrap items-center gap-4 mt-3">
          <label className="flex items-center gap-2 text-sm font-semibold text-ink cursor-pointer">
            <input data-testid="web-trusted-checkbox" type="checkbox" checked={webForm.trusted} onChange={(e) => setWebForm({ ...webForm, trusted: e.target.checked })} className="w-4 h-4 accent-brand" />
            <ShieldCheck size={15} className="text-success" /> Trust this source (auto-post its sales)
          </label>
          <label className="flex items-center gap-2 text-sm font-semibold text-ink cursor-pointer">
            <input data-testid="web-autoscan-checkbox" type="checkbox" checked={webForm.auto_scan} onChange={(e) => setWebForm({ ...webForm, auto_scan: e.target.checked })} className="w-4 h-4 accent-brand" />
            <RefreshCw size={15} className="text-brand" /> Auto-scan
          </label>
          {webForm.auto_scan && (
            <>
              <select data-testid="web-weekday-select" value={webForm.scan_weekday} onChange={(e) => setWebForm({ ...webForm, scan_weekday: Number(e.target.value) })} className="ainput !h-9 !w-auto text-sm">
                {DAYS.map((d) => <option key={d.v} value={d.v}>Every {d.label}</option>)}
              </select>
              <select data-testid="web-hour-select" value={webForm.scan_hour} onChange={(e) => setWebForm({ ...webForm, scan_hour: Number(e.target.value) })} className="ainput !h-9 !w-auto text-sm">
                {HOURS.map((h) => <option key={h.v} value={h.v}>at {h.label} ET</option>)}
              </select>
            </>
          )}
          <button data-testid="web-scan-btn" onClick={addAndScan} disabled={scanning} className="btn-primary disabled:opacity-60 ml-auto"><Globe size={16} /> {scanning ? "Reading…" : "Add & Scan"}</button>
        </div>

        {sources.length > 0 && (
          <div className="mt-5 space-y-2">
            <p className="text-xs font-bold uppercase tracking-wider text-ink-muted">Saved publications ({sources.length})</p>
            {sources.map((s) => (
              <div key={s.id} data-testid={`web-source-${s.id}`} className="p-3 rounded-xl border border-line">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-ink text-sm truncate flex items-center gap-2">
                      {s.label}
                      {s.trusted && <span className="text-[10px] font-bold bg-success/10 text-success px-1.5 py-0.5 rounded-full">TRUSTED</span>}
                      {s.auto_scan && <span className="text-[10px] font-bold bg-brand-light text-brand px-1.5 py-0.5 rounded-full">EVERY {dayLabel(s.scan_weekday ?? 6).toUpperCase()} {hourLabel(s.scan_hour ?? 6)}</span>}
                    </p>
                    <p className="text-xs text-ink-muted truncate">{s.url}{s.last_scanned ? ` · last scan ${s.last_count} sales` : " · not scanned yet"}</p>
                    {s.instructions ? <p className="text-xs text-ink-soft italic truncate mt-0.5">“{s.instructions}”</p> : null}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button data-testid={`web-rescan-${s.id}`} onClick={() => scan({ source_id: s.id })} disabled={scanning} className="btn-ghost h-9 px-3 text-xs disabled:opacity-60"><RefreshCw size={14} /> Scan now</button>
                    <button data-testid={`web-delete-${s.id}`} onClick={() => delSource(s.id)} className="iconbtn text-brand"><Trash2 size={16} /></button>
                  </div>
                </div>
                <div className="flex gap-4 mt-2 pt-2 border-t border-line">
                  <label className="flex items-center gap-1.5 text-xs font-semibold text-ink-soft cursor-pointer">
                    <input data-testid={`web-source-trusted-${s.id}`} type="checkbox" checked={!!s.trusted} onChange={(e) => toggleSource(s.id, "trusted", e.target.checked)} className="w-3.5 h-3.5 accent-brand" /> Trusted
                  </label>
                  <label className="flex items-center gap-1.5 text-xs font-semibold text-ink-soft cursor-pointer">
                    <input data-testid={`web-source-autoscan-${s.id}`} type="checkbox" checked={!!s.auto_scan} onChange={(e) => toggleSource(s.id, "auto_scan", e.target.checked)} className="w-3.5 h-3.5 accent-brand" /> Auto-scan
                  </label>
                  {s.auto_scan && (
                    <>
                      <select data-testid={`web-source-weekday-${s.id}`} value={s.scan_weekday ?? 6} onChange={(e) => toggleSource(s.id, "scan_weekday", Number(e.target.value))} className="text-xs border border-line rounded-lg px-2 py-1 bg-white">
                        {DAYS.map((d) => <option key={d.v} value={d.v}>Every {d.label}</option>)}
                      </select>
                      <select data-testid={`web-source-hour-${s.id}`} value={s.scan_hour ?? 6} onChange={(e) => toggleSource(s.id, "scan_hour", Number(e.target.value))} className="text-xs border border-line rounded-lg px-2 py-1 bg-white">
                        {HOURS.map((h) => <option key={h.v} value={h.v}>at {h.label} ET</option>)}
                      </select>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Papers list */}
      <div className="bg-white rounded-2xl border border-line shadow-soft p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-bold text-lg text-ink">Received papers ({papers.length})</h3>
          <div className="flex items-center gap-2">
            <button data-testid="clear-queue-btn" onClick={clearQueue} disabled={clearing} className={`btn-ghost h-9 px-3 text-xs disabled:opacity-60 ${confirmClear ? "!text-brand font-bold" : ""}`}><Trash2 size={14} /> {confirmClear ? "Tap again to confirm" : "Clear review queue"}</button>
            <button data-testid="expire-now-btn" onClick={expireNow} className="btn-ghost h-9 px-3 text-xs"><Clock size={14} /> Delete expired now</button>
          </div>
        </div>
        {papers.length === 0 ? <p className="text-ink-muted text-sm">No papers yet. Upload one above.</p> : (
          <div className="space-y-2">
            {papers.map((p) => (
              <div key={p.id} data-testid={`paper-row-${p.id}`} className={`flex items-center justify-between gap-3 p-3 rounded-xl border transition-all ${active === p.id ? "border-brand bg-brand-light" : "border-line"}`}>
                <button onClick={() => openPaper(p.id)} className="flex items-center gap-3 text-left flex-1">
                  <div className="w-10 h-10 rounded-lg bg-blush flex items-center justify-center"><FileText className="text-brand" size={18} /></div>
                  <div>
                    <p className="font-semibold text-ink text-sm">{p.filename}</p>
                    <p className="text-xs text-ink-muted">
                      {(p.status === "scanning" || p.status === "processing") && <span className="text-brand font-bold">Reading… </span>}
                      {p.status === "error" && <span className="text-brand font-bold">Couldn't read · </span>}
                      {p.sale_count} sales{p.skipped ? ` · ${p.skipped} dup skipped` : ""} · {p.source === "web" ? "web" : "email/upload"} · {p.autopost ? "auto-posted" : "review"} · {new Date(p.created_at).toLocaleDateString()}
                    </p>
                    {p.community_source && (
                      <span
                        data-testid={`paper-community-${p.id}`}
                        title={p.community_source === "default"
                          ? "No community name found in the folder, subject or body — defaulted to Monsey, NY"
                          : `Community "${p.community_detected}" detected from the email ${p.community_source}`}
                        className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-gold/15 text-gold-text text-[11px] font-bold"
                      >
                        <MapPin size={11} />
                        {p.community_detected || "Monsey, NY"}
                        <span className="font-semibold text-gold-text/70">· via {p.community_source === "default" ? "default" : p.community_source}</span>
                      </span>
                    )}
                  </div>
                </button>
                {p.gridfs_id ? (
                  <button data-testid={`paper-view-${p.id}`} onClick={() => viewPdf(p.id)} className="btn-ghost h-9 px-3 text-xs"><Eye size={14} /> PDF</button>
                ) : p.source_url ? (
                  <button data-testid={`paper-view-${p.id}`} onClick={() => window.open(p.source_url, "_blank")} className="btn-ghost h-9 px-3 text-xs"><Globe size={14} /> Open</button>
                ) : null}
                <button data-testid={`paper-delete-${p.id}`} onClick={() => delPaper(p.id)} className="iconbtn text-brand"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Draft cards for active paper */}
      {active && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-lg text-ink flex items-center gap-2">
              Extracted sale cards ({sales.length})
              {(() => {
                const missing = sales.filter((s) => !s.valid_to).length;
                return missing ? (
                  <span data-testid="paper-noexpiry-count" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gold/15 text-gold-text text-xs font-bold">
                    <AlertTriangle size={12} /> {missing} missing expiry
                  </span>
                ) : null;
              })()}
            </h3>
            {(() => {
              const ap = papers.find((p) => p.id === active) || {};
              if (ap.gridfs_id) return <button onClick={() => viewPdf(active)} className="text-brand font-bold text-sm flex items-center gap-1" data-testid="review-view-pdf"><Eye size={15} /> View original PDF</button>;
              if (ap.source_url) return <button onClick={() => window.open(ap.source_url, "_blank")} className="text-brand font-bold text-sm flex items-center gap-1" data-testid="review-view-source"><Globe size={15} /> Open source</button>;
              return null;
            })()}
          </div>
          {sales.length === 0 ? <p className="text-ink-muted text-sm">No sales extracted from this paper.</p> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {sales.map((s) => (
                <div key={s.id} data-testid={`paper-sale-${s.id}`} className="bg-white rounded-2xl border border-line shadow-soft overflow-hidden">
                  <div className="relative h-28 bg-blush">
                    <img src={categoryImage(s)} alt="" className="w-full h-full object-cover" />
                    {s.page ? <button data-testid={`paper-sale-page-${s.id}`} onClick={() => openOriginal(s.page)} title={`Open the original at page ${s.page}`} className="absolute top-2 left-2 text-xs font-bold px-2 py-0.5 rounded-full bg-ink/80 text-white hover:bg-ink transition-colors flex items-center gap-1"><FileText size={11} /> Page {s.page}</button> : null}
                    {s.is_giveaway && <span data-testid={`paper-sale-giveaway-${s.id}`} className="absolute bottom-2 left-2 text-xs font-extrabold px-2 py-0.5 rounded-full bg-gold text-white flex items-center gap-1"><Gift size={11} /> Free Giveaway</span>}
                    <span className={`absolute top-2 right-2 text-xs font-bold px-2 py-0.5 rounded-full ${s.status === "approved" ? "bg-success/90 text-white" : "bg-gold/90 text-white"}`}>{s.status === "approved" ? "LIVE" : "PENDING"}</span>
                  </div>
                  <div className="p-3 space-y-1">
                    <p className="text-xs font-bold uppercase tracking-wide text-brand">{s.store || "Unknown store"}</p>
                    <p className="font-bold text-ink text-sm leading-snug">{s.title}</p>
                    <p className="text-xs text-ink-soft">{s.is_giveaway ? "Free giveaway" : `${s.price_note}${s.percent_off ? ` · ${s.up_to ? "up to " : ""}${s.percent_off}% off` : ""}`}</p>
                    <p className="text-xs text-ink-muted capitalize">{s.category} · {s.location}{s.page ? ` · page ${s.page}` : ""}</p>
                    {s.valid_to ? (
                      <p className="text-xs text-ink-muted flex items-center gap-1"><Clock size={11} /> Ends {s.valid_to}{s.valid_from ? ` (from ${s.valid_from})` : ""}</p>
                    ) : (
                      <p data-testid={`paper-noexpiry-${s.id}`} className="text-xs font-bold text-gold-text flex items-center gap-1"><AlertTriangle size={11} /> No expiry listed — review</p>
                    )}
                    {s.status !== "approved" && (
                      <div className="pt-2 space-y-2">
                        <input
                          data-testid={`paper-reject-reason-${s.id}`}
                          value={rejectReasons[s.id] || ""}
                          onChange={(e) => setRejectReasons((r) => ({ ...r, [s.id]: e.target.value }))}
                          placeholder="Why reject? e.g. not a clothing or food deal"
                          className="w-full h-8 px-2.5 rounded-lg border border-line bg-cream text-xs outline-none focus:border-brand"
                        />
                        <div className="flex gap-2">
                          <button data-testid={`paper-approve-${s.id}`} onClick={() => approve(s.id)} className="flex-1 h-9 rounded-full bg-brand text-white text-xs font-bold flex items-center justify-center gap-1 hover:bg-brand-hover"><Check size={14} /> Approve</button>
                          <button data-testid={`paper-reject-${s.id}`} onClick={() => reject(s.id)} className="h-9 px-3 rounded-full border border-line text-ink-soft text-xs font-bold flex items-center gap-1 hover:border-brand/30"><X size={14} /> Reject</button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <AdminStyles />
    </div>
  );
}

function prettySource(src) {
  if (!src) return "Uploaded PDFs";
  if (src.startsWith("gmail:")) return `Gmail — ${src.slice(6)}`;
  if (src.startsWith("upload:")) return `Upload — ${src.slice(7)}`;
  if (src.startsWith("http")) { try { return new URL(src).hostname.replace(/^www\./, ""); } catch { return src; } }
  if (/^[0-9a-f-]{30,}$/i.test(src)) return "Uploaded PDF";
  return src;
}

function LearningReport({ version }) {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/admin/rejection-reasons").then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load, version]);

  const remove = async (id) => {
    await api.delete(`/admin/rejection-reasons/${id}`);
    toast.success("Removed — the AI will no longer avoid this");
    load();
  };

  if (items.length === 0) return null;
  const groups = {};
  for (const it of items) { const k = it.source_label || it.source_key || ""; (groups[k] = groups[k] || []).push(it); }

  return (
    <div className="bg-white rounded-2xl border border-line shadow-soft p-5" data-testid="learning-report">
      <h3 className="font-heading font-bold text-lg text-ink flex items-center gap-2"><Sparkles size={18} className="text-brand" /> What the AI has learned <span className="text-sm text-ink-muted font-normal">({items.length})</span></h3>
      <p className="text-xs text-ink-muted mt-0.5 mb-3">Deals you rejected with a reason. The AI skips similar items the next time each source is scanned. Remove any to stop avoiding it.</p>
      <div className="space-y-4">
        {Object.entries(groups).map(([src, list]) => (
          <div key={src || "unknown"} data-testid={`learn-source-${src || "unknown"}`}>
            <p className="text-xs font-bold uppercase tracking-wide text-ink-soft">{prettySource(src)}</p>
            <div className="space-y-1.5 mt-1.5">
              {list.map((it) => (
                <div key={it.id} data-testid={`reason-row-${it.id}`} className="flex items-start justify-between gap-2 bg-cream rounded-lg px-3 py-2 border border-line">
                  <div className="min-w-0">
                    <p className="text-sm text-ink font-semibold break-words">{it.reason}</p>
                    <p className="text-xs text-ink-muted truncate">{it.store ? `${it.store} · ` : ""}“{it.sale_title}”</p>
                  </div>
                  <button data-testid={`reason-remove-${it.id}`} onClick={() => remove(it.id)} title="Remove this lesson" className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-ink-muted hover:bg-brand/10 hover:text-brand transition-all"><X size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GmailConnect({ onIngested }) {
  const [st, setSt] = useState(null);
  const [senders, setSenders] = useState([]);
  const [newSender, setNewSender] = useState("");
  const [busy, setBusy] = useState(false);
  const [rp, setRp] = useState({ date: "", mode: "since", sender: "" });
  const [rpBusy, setRpBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/gmail/status").then(({ data }) => { setSt(data); setSenders(data.senders || []); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("gmail");
    if (p === "connected") { toast.success("Gmail connected!"); load(); window.history.replaceState({}, "", "/admin"); }
    else if (p === "error") { toast.error("Gmail connection failed — please try again"); window.history.replaceState({}, "", "/admin"); }
  }, [load]);

  const connect = async () => {
    try {
      const { data } = await api.post("/admin/gmail/authorize");
      window.location.href = data.url;
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not start Gmail connection");
    }
  };

  const saveSenders = async (list) => {
    setSenders(list);
    try { const { data } = await api.post("/admin/gmail/senders", { senders: list }); setSenders(data.senders); }
    catch { toast.error("Could not save senders"); load(); }
  };
  const addSender = () => {
    const v = newSender.trim().toLowerCase();
    if (!v || !v.includes("@")) { toast.error("Enter a valid email address"); return; }
    if (senders.includes(v)) { toast.error("Already added"); return; }
    saveSenders([...senders, v]); setNewSender("");
  };

  const saveSettings = async (patch) => {
    const current = st.scan_weekdays || (st.scan_weekday != null ? [st.scan_weekday] : []);
    const body = { auto_pull: st.auto_pull, scan_weekdays: current, scan_hour: st.scan_hour, ...patch };
    setSt({ ...st, ...body });
    try { const { data } = await api.post("/admin/gmail/settings", body); setSt(data); }
    catch { toast.error("Could not save schedule"); load(); }
  };

  const toggleDay = (v) => {
    const current = st.scan_weekdays || (st.scan_weekday != null ? [st.scan_weekday] : []);
    const next = current.includes(v) ? current.filter((d) => d !== v) : [...current, v];
    saveSettings({ scan_weekdays: next });
  };

  const pullNow = async () => {
    setBusy(true);
    toast.loading("Checking Gmail for new papers…", { id: "gmail-pull" });
    try {
      const { data } = await api.post("/admin/gmail/pull");
      const dupPdf = data.skipped_dupes ? `, ${data.skipped_dupes} already-processed PDF(s) skipped` : "";
      if (data.papers === 0) toast.info(`No new paper PDFs found (checked ${data.checked} emails)${dupPdf}`, { id: "gmail-pull" });
      else toast.success(`Imported ${data.papers} paper(s) · ${data.sales} sales${data.skipped ? `, ${data.skipped} duplicate sales skipped` : ""}${dupPdf}`, { id: "gmail-pull" });
      onIngested && onIngested();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Gmail pull failed", { id: "gmail-pull" }); }
    finally { setBusy(false); }
  };

  const disconnect = async () => {
    await api.post("/admin/gmail/disconnect").catch(() => {});
    toast.success("Gmail disconnected");
    load();
  };

  const rePull = async () => {
    if (!rp.date) { toast.error("Pick a date first"); return; }
    setRpBusy(true);
    toast.loading("Re-reading past emails…", { id: "gmail-repull" });
    try {
      const { data } = await api.post("/admin/gmail/repull", { date: rp.date, mode: rp.mode, sender: rp.sender.trim() || undefined });
      const dup = data.skipped ? `, ${data.skipped} duplicate deal(s) skipped` : "";
      if (data.papers === 0) toast.info(`No matching PDFs found (checked ${data.checked} emails)${dup}`, { id: "gmail-repull" });
      else toast.success(`Re-read ${data.papers} paper(s) · ${data.sales} new sale(s)${dup}`, { id: "gmail-repull" });
      onIngested && onIngested();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Re-pull failed", { id: "gmail-repull" }); }
    finally { setRpBusy(false); }
  };

  if (!st) return null;

  return (
    <div className="bg-white rounded-2xl border border-line shadow-soft p-5" data-testid="gmail-connect-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <h3 className="font-heading font-bold text-lg text-ink flex items-center gap-2"><Mail size={18} className="text-brand" /> Connect Gmail</h3>
        {st.connected && <span className="text-xs font-bold bg-success/10 text-success px-2.5 py-1 rounded-full flex items-center gap-1"><Check size={12} /> {st.email}</span>}
      </div>

      {!st.configured && (
        <div className="rounded-xl bg-gold/10 border border-gold/30 p-4 text-sm text-ink-soft" data-testid="gmail-not-configured">
          Gmail isn't set up yet. Your Google Client ID &amp; Secret need to be added first — send them to your SIBLINGO builder and this will switch on automatically.
        </div>
      )}

      {st.configured && !st.connected && (
        <div>
          <p className="text-sm text-ink-muted mb-3">Link your inbox once, and SIBLINGO can pull the weekly paper PDFs straight from your emails into the review queue below.</p>
          <button data-testid="gmail-connect-btn" onClick={connect} className="btn-primary"><Mail size={16} /> Connect Gmail account</button>
        </div>
      )}

      {st.configured && st.connected && (
        <div className="space-y-4">
          <div data-testid="gmail-label-info" className="rounded-2xl bg-cream border border-line p-4">
            <p className="text-sm font-semibold text-ink mb-1">Pulling from your Gmail label folders</p>
            <p className="text-xs text-ink-muted">
              SIBLINGO reads every email filed under your <span className="font-bold text-ink">“{st.label_parent || "uptoo"}”</span> Gmail label and its
              area subfolders (e.g. <span className="font-semibold">{st.label_parent || "uptoo"}/Monsey</span>, <span className="font-semibold">{st.label_parent || "uptoo"}/Lakewood</span>).
              Each deal is automatically tagged with its subfolder area. Just file the papers into the right subfolder in Gmail — no sender list needed.
            </p>
          </div>

          <div className="pt-3 border-t border-line">
            <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold text-ink" data-testid="gmail-autopull-label">
              <input type="checkbox" data-testid="gmail-autopull-checkbox" checked={!!st.auto_pull} onChange={(e) => saveSettings({ auto_pull: e.target.checked })} className="w-4 h-4 accent-brand" />
              <RefreshCw size={15} /> Auto-check on the days I pick
            </label>
            {st.auto_pull && (
              <div className="mt-3 space-y-2">
                <p className="text-xs text-ink-muted">Pick any days — check them all or skip some.</p>
                <div className="flex flex-wrap gap-2" data-testid="gmail-days">
                  {DAYS.map((d) => {
                    const active = (st.scan_weekdays || []).includes(d.v);
                    return (
                      <button
                        key={d.v}
                        type="button"
                        data-testid={`gmail-day-${d.v}`}
                        aria-pressed={active}
                        onClick={() => toggleDay(d.v)}
                        className={`h-9 px-3 rounded-full text-sm font-semibold border transition-all ${active ? "bg-brand text-white border-brand" : "bg-white text-ink border-line hover:border-brand/40"}`}
                      >
                        {d.label.slice(0, 3)}
                      </button>
                    );
                  })}
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <span className="text-sm text-ink-soft">Check in</span>
                  <select data-testid="gmail-hour-select" value={st.scan_hour ?? 6} onChange={(e) => saveSettings({ scan_hour: Number(e.target.value) })} className="ainput !h-9 !w-auto text-sm">
                    {HOURS.map((h) => <option key={h.v} value={h.v}>at {h.label} ET</option>)}
                  </select>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button data-testid="gmail-pull-btn" onClick={pullNow} disabled={busy} className="btn-primary">
              <RefreshCw size={16} className={busy ? "animate-spin" : ""} /> {busy ? "Checking…" : "Pull from Gmail now"}
            </button>
            <button data-testid="gmail-disconnect-btn" onClick={disconnect} className="text-sm font-semibold text-ink-muted hover:text-brand">Disconnect</button>
            {st.last_pull_at && <span className="text-xs text-ink-muted">Last checked {new Date(st.last_pull_at).toLocaleString()} · {st.last_count} sales</span>}
          </div>

          <div className="pt-3 border-t border-line" data-testid="gmail-repull">
            <p className="text-sm font-semibold text-ink mb-1 flex items-center gap-2"><Clock size={15} /> Re-pull past emails</p>
            <p className="text-xs text-ink-muted mb-2">Re-read older emails you may have missed. It re-scans them even if seen before, and skips any deal already in your queue.</p>
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <label className="block text-xs text-ink-muted mb-1">Date</label>
                <input type="date" data-testid="gmail-repull-date" className="ainput !h-9 !w-auto text-sm" value={rp.date} onChange={(e) => setRp({ ...rp, date: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs text-ink-muted mb-1">Range</label>
                <select data-testid="gmail-repull-mode" className="ainput !h-9 !w-auto text-sm" value={rp.mode} onChange={(e) => setRp({ ...rp, mode: e.target.value })}>
                  <option value="since">From this date onwards</option>
                  <option value="day">Just this one day</option>
                </select>
              </div>
              <div className="flex-1 min-w-[180px]">
                <label className="block text-xs text-ink-muted mb-1">From (optional)</label>
                <input type="email" data-testid="gmail-repull-sender" className="ainput !h-9 text-sm w-full" placeholder="any of your senders" value={rp.sender} onChange={(e) => setRp({ ...rp, sender: e.target.value })} />
              </div>
              <button data-testid="gmail-repull-btn" onClick={rePull} disabled={rpBusy} className="h-9 px-4 rounded-full text-sm font-semibold border border-brand text-brand hover:bg-brand hover:text-white transition-colors flex items-center gap-1.5 shrink-0 disabled:opacity-60">
                <RefreshCw size={15} className={rpBusy ? "animate-spin" : ""} /> {rpBusy ? "Reading…" : "Pull again"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CategoriesManager() {
  const { options, saveOptions } = useSite();
  const GROUPS = [
    { g: "sale_category", label: "Sale categories", hint: "Used in Sale Manager & clothing sale filters" },
    { g: "product_gender", label: "Gender", hint: "Used in Deal Manager & AI seed library" },
    { g: "occasion", label: "Occasions", hint: "Used in Deal Manager & Collections" },
    { g: "collection_whom", label: "Collection audience", hint: "Who a matching set is for" },
    { g: "match_type", label: "Match types", hint: "How a family set coordinates" },
    { g: "department", label: "Marketplace departments", hint: "Chips shoppers see on All Local Sales" },
    { g: "community", label: "Communities", hint: "Locations shoppers can filter by" },
  ];
  return (
    <div className="space-y-6">
      <div className="bg-brand-light border border-brand/20 rounded-2xl p-4 text-sm text-ink-soft flex items-center gap-2">
        <Tags className="text-brand" size={18} /> Add or remove any dropdown option. Changes go live instantly for shoppers. Existing sales keep their old category even if you remove it here.
      </div>
      {GROUPS.map((grp) => (
        <OptionGroupEditor key={grp.g} grp={grp} list={options[grp.g] || []} onSave={(list) => saveOptions(grp.g, list)} />
      ))}
      <AdminStyles />
    </div>
  );
}

function OptionGroupEditor({ grp, list, onSave }) {
  const [val, setVal] = useState("");
  const add = () => {
    const v = val.trim();
    if (!v) return;
    if (list.includes(v)) { toast.error("That option already exists"); return; }
    onSave([...list, v]);
    setVal("");
    toast.success(`Added “${v}”`);
  };
  const remove = (item) => { onSave(list.filter((x) => x !== item)); toast.success(`Removed “${item}”`); };
  return (
    <div className="bg-white rounded-2xl border border-line shadow-soft p-5 space-y-3" data-testid={`cat-group-${grp.g}`}>
      <div>
        <h3 className="font-heading font-bold text-lg text-ink">{grp.label}</h3>
        <p className="text-xs text-ink-muted">{grp.hint}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {list.length === 0 && <p className="text-sm text-ink-muted">No options yet — add one below.</p>}
        {list.map((item) => (
          <span key={item} data-testid={`cat-chip-${grp.g}-${item}`} className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1.5 rounded-full bg-cream border border-line text-sm font-semibold text-ink">
            {item}
            <button data-testid={`cat-remove-${grp.g}-${item}`} onClick={() => remove(item)} className="w-5 h-5 rounded-full flex items-center justify-center hover:bg-brand/10 text-ink-muted hover:text-brand transition-all"><X size={13} /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          data-testid={`cat-input-${grp.g}`}
          className="ainput flex-1"
          placeholder="New option…"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
        />
        <button data-testid={`cat-add-${grp.g}`} onClick={add} className="btn-primary shrink-0"><Plus size={16} /> Add</button>
      </div>
    </div>
  );
}

function SizeCharts() {
  const [brands, setBrands] = useState([]);
  const [types, setTypes] = useState([]);
  const [charts, setCharts] = useState([]);
  const [form, setForm] = useState({ level: "brand", retailer: "", brand: "", product_name: "", style_sku: "", product_url: "", clothing_type: "tops", gender_group: "boys", status: "verified", source: "", date_verified: "", rows: [{ _k: crypto.randomUUID(), size: "", min: "", max: "" }] });
  const [newBrand, setNewBrand] = useState("");
  const [newType, setNewType] = useState({ key: "", label: "", primary: "chest" });
  const [importing, setImporting] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [exporting, setExporting] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/admin/size-brands").then(({ data }) => setBrands(data)).catch(() => {});
    api.get("/admin/size-clothing-types").then(({ data }) => setTypes(data)).catch(() => {});
    api.get("/admin/size-charts").then(({ data }) => setCharts(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const primary = (types.find((t) => t.key === form.clothing_type) || {}).primary || "chest";
  const setRow = (i, k, v) => setForm({ ...form, rows: form.rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)) });
  const addRow = () => setForm({ ...form, rows: [...form.rows, { _k: crypto.randomUUID(), size: "", min: "", max: "" }] });
  const delRow = (i) => setForm({ ...form, rows: form.rows.filter((_, j) => j !== i) });

  const saveChart = async () => {
    const rows = form.rows.filter((r) => String(r.size).trim()).map((r) => ({
      size: String(r.size).trim(),
      [`${primary}_min`]: String(r.min).trim(),
      [`${primary}_max`]: String(r.max).trim(),
    }));
    if (!form.brand.trim() || !form.clothing_type || rows.length === 0) { toast.error("Brand, clothing type and at least one size row are required."); return; }
    if (form.status === "verified" && (!form.source.trim() || !form.date_verified.trim())) { toast.error("Verified charts need a source and date verified."); return; }
    try {
      await api.post("/admin/size-charts", { ...form, rows });
      toast.success("Size chart saved.");
      setForm({ level: form.level, retailer: "", brand: "", product_name: "", style_sku: "", product_url: "", clothing_type: form.clothing_type, gender_group: form.gender_group, status: "verified", source: "", date_verified: "", rows: [{ _k: crypto.randomUUID(), size: "", min: "", max: "" }] });
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/size-charts/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = "siblingo_size_charts.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Exported the full chart library.");
    } catch { toast.error("Export failed."); }
    finally { setExporting(false); }
  };

  const setStatus = async (id, status) => {
    try { await api.post(`/admin/size-charts/${id}/status`, { status }); toast.success(`Marked ${status.replace("_", " ")}.`); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const importCsv = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/admin/size-charts/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.charts_imported} chart(s).`); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Import failed."); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const addBrand = async () => {
    if (!newBrand.trim()) return;
    await api.post("/admin/size-brands", { name: newBrand.trim() }); setNewBrand(""); load(); toast.success("Brand added.");
  };
  const addType = async () => {
    if (!newType.key.trim() || !newType.label.trim()) { toast.error("Type key and label are required."); return; }
    await api.post("/admin/size-clothing-types", { ...newType, order: (types.length + 1) }); setNewType({ key: "", label: "", primary: "chest" }); load(); toast.success("Clothing type added.");
  };

  return (
    <div className="space-y-6" data-testid="admin-sizecharts">
      <div className="rounded-2xl bg-gold/10 border border-gold/30 p-4 text-sm text-ink">
        <b>Product Size Chart Library.</b> A permanent, versioned library at three levels (brand, product-family, individual product). Charts are <b>verified</b>, <b>needs review</b>, or <b>retired</b> — only <b>verified</b> charts feed size recommendations. Auto-scraped charts from deal submissions land as "needs review" for you to confirm. Verified charts are never deleted (retired instead), and updates create a new version.
      </div>

      {/* Add / import charts */}
      <div className="card">
        <h3 className="font-heading font-bold text-lg text-ink mb-3">Add a size chart</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="lbl">Level</label>
            <select data-testid="chart-level" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} className="fld">
              <option value="brand">Brand-level (consistent across products)</option>
              <option value="family">Product-family (several styles share it)</option>
              <option value="product">Individual product</option>
            </select>
          </div>
          <div>
            <label className="lbl">Status</label>
            <select data-testid="chart-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="fld">
              <option value="verified">Verified (feeds recommendations)</option>
              <option value="needs_review">Needs review</option>
              <option value="retired">Retired</option>
            </select>
          </div>
          <div>
            <label className="lbl">Retailer</label>
            <input data-testid="chart-retailer" value={form.retailer} onChange={(e) => setForm({ ...form, retailer: e.target.value })} placeholder="Old Navy (store)" className="fld" />
          </div>
          <div>
            <label className="lbl">Actual brand</label>
            <input data-testid="chart-brand" list="sc-brands" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Gap Kids" className="fld" />
            <datalist id="sc-brands">{brands.map((b) => <option key={b.id} value={b.name} />)}</datalist>
          </div>
          {form.level !== "brand" && (
            <>
              <div>
                <label className="lbl">Product name</label>
                <input data-testid="chart-product-name" value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} placeholder="Boys Polo Shirt" className="fld" />
              </div>
              <div>
                <label className="lbl">Style # / SKU</label>
                <input data-testid="chart-style-sku" value={form.style_sku} onChange={(e) => setForm({ ...form, style_sku: e.target.value })} placeholder="ON-12345" className="fld" />
              </div>
              <div className="sm:col-span-2">
                <label className="lbl">Product URL</label>
                <input data-testid="chart-product-url" value={form.product_url} onChange={(e) => setForm({ ...form, product_url: e.target.value })} placeholder="https://…" className="fld" />
              </div>
            </>
          )}
          <div>
            <label className="lbl">Clothing type</label>
            <select data-testid="chart-type" value={form.clothing_type} onChange={(e) => setForm({ ...form, clothing_type: e.target.value })} className="fld">
              {types.map((t) => <option key={t.key} value={t.key}>{t.label} (matched on {t.primary})</option>)}
            </select>
          </div>
          <div>
            <label className="lbl">Gender / age group</label>
            <select data-testid="chart-gender" value={form.gender_group} onChange={(e) => setForm({ ...form, gender_group: e.target.value })} className="fld">
              {["boys", "girls", "unisex", "baby"].map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div>
            <label className="lbl">Date verified</label>
            <input data-testid="chart-date" type="date" value={form.date_verified} onChange={(e) => setForm({ ...form, date_verified: e.target.value })} className="fld" />
          </div>
          <div className="sm:col-span-2">
            <label className="lbl">Source (chart URL or document)</label>
            <input data-testid="chart-source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="https://oldnavy.gap.com/... size chart" className="fld" />
          </div>
        </div>

        <p className="text-xs font-bold uppercase tracking-wider text-ink-muted mt-4 mb-1">Size rows — {primary} range (inches)</p>
        <div className="space-y-2">
          {form.rows.map((r, i) => (
            <div key={r._k || i} data-testid={`chart-row-${i}`} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center">
              <input data-testid={`chart-row-size-${i}`} value={r.size} onChange={(e) => setRow(i, "size", e.target.value)} placeholder="Size (e.g. 8 Husky)" className="fld" />
              <input data-testid={`chart-row-min-${i}`} value={r.min} onChange={(e) => setRow(i, "min", e.target.value)} placeholder={`${primary} min`} className="fld" />
              <input data-testid={`chart-row-max-${i}`} value={r.max} onChange={(e) => setRow(i, "max", e.target.value)} placeholder={`${primary} max`} className="fld" />
              <button onClick={() => delRow(i)} className="iconbtn text-brand"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
        <button onClick={addRow} className="btn-ghost mt-2"><Plus size={15} /> Add row</button>
        <div className="mt-3 flex flex-wrap gap-2">
          <button data-testid="chart-save-btn" onClick={saveChart} className="btn-primary"><Check size={16} /> Save chart</button>
          <label className="btn-ghost cursor-pointer">
            <Upload size={15} /> {importing ? "Importing…" : "Import CSV"}
            <input ref={fileRef} data-testid="chart-import-file" type="file" accept=".csv,text/csv" onChange={importCsv} className="hidden" disabled={importing} />
          </label>
          <button data-testid="chart-export-btn" onClick={exportCsv} disabled={exporting} className="btn-ghost disabled:opacity-60"><FileText size={15} /> {exporting ? "Exporting…" : "Export CSV"}</button>
        </div>
        <p className="text-[11px] text-ink-muted mt-2">CSV columns (full Siblingo format): level, retailer, brand, product_name, style_sku, product_url, clothing_type, gender_group, status, source, screenshot_url, date_verified, chart_version, fit_feedback, size, chest_min, chest_max, waist_min, waist_max, height_min, height_max, inseam_min, inseam_max, foot_min, foot_max, weight_min, weight_max. The simple format (brand, clothing_type, size, *_min/*_max) still imports.</p>
      </div>

      {/* Manage brands + types */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="card">
          <h3 className="font-heading font-bold text-lg text-ink mb-2">Brands</h3>
          <div className="flex gap-2">
            <input data-testid="new-brand-input" value={newBrand} onChange={(e) => setNewBrand(e.target.value)} placeholder="Add a brand" className="fld" />
            <button data-testid="add-brand-btn" onClick={addBrand} className="btn-primary shrink-0"><Plus size={15} /></button>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {brands.map((b) => (
              <span key={b.id} className="text-xs font-semibold bg-cream border border-line rounded-full pl-3 pr-1 py-1 flex items-center gap-1">
                {b.name}
                <button onClick={async () => { await api.delete(`/admin/size-brands/${b.id}`); load(); }} className="p-0.5 rounded-full text-ink-muted hover:text-brand"><X size={13} /></button>
              </span>
            ))}
          </div>
        </div>
        <div className="card">
          <h3 className="font-heading font-bold text-lg text-ink mb-2">Clothing types</h3>
          <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2">
            <input data-testid="new-type-key" value={newType.key} onChange={(e) => setNewType({ ...newType, key: e.target.value })} placeholder="key (e.g. suits)" className="fld" />
            <input data-testid="new-type-label" value={newType.label} onChange={(e) => setNewType({ ...newType, label: e.target.value })} placeholder="Label" className="fld" />
            <select value={newType.primary} onChange={(e) => setNewType({ ...newType, primary: e.target.value })} className="fld">
              {["chest", "waist", "height", "foot"].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <button data-testid="add-type-btn" onClick={addType} className="btn-primary shrink-0"><Plus size={15} /></button>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {types.map((t) => (
              <span key={t.id} className="text-xs font-semibold bg-cream border border-line rounded-full pl-3 pr-1 py-1 flex items-center gap-1">
                {t.label}
                <button onClick={async () => { await api.delete(`/admin/size-clothing-types/${t.id}`); load(); }} className="p-0.5 rounded-full text-ink-muted hover:text-brand"><X size={13} /></button>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Existing charts */}
      <div className="card">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
          <h3 className="font-heading font-bold text-lg text-ink">Chart library ({charts.filter((c) => statusFilter === "all" || (c.status || "verified") === statusFilter).length}{statusFilter !== "all" ? ` ${statusFilter.replace("_", " ")}` : ""})</h3>
          <div className="flex gap-1.5">
            {["all", "verified", "needs_review", "retired"].map((s) => (
              <button key={s} data-testid={`chart-filter-${s}`} onClick={() => setStatusFilter(s)}
                className={`px-2.5 py-1 rounded-full text-xs font-bold border transition-all ${statusFilter === s ? "bg-brand text-white border-brand" : "bg-white border-line text-ink-soft"}`}>
                {s === "all" ? "All" : s.replace("_", " ")}
              </button>
            ))}
          </div>
        </div>
        {charts.length === 0 && <p className="text-sm text-ink-muted">No charts yet. Add, import, or let partner deal submissions auto-scrape them.</p>}
        <div className="space-y-2">
          {charts.filter((c) => statusFilter === "all" || (c.status || "verified") === statusFilter).map((c) => {
            const st = c.status || "verified";
            const badge = st === "verified" ? "bg-success/10 text-success" : st === "needs_review" ? "bg-gold/15 text-gold-text" : "bg-ink/10 text-ink-muted";
            return (
              <div key={c.id} data-testid={`chart-item-${c.id}`} className={`rounded-xl border px-3 py-2 ${st === "needs_review" ? "border-gold/40 bg-gold/5" : "border-line"}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-ink text-sm flex items-center gap-2 flex-wrap">
                      {c.brand} · {c.clothing_type} · {c.gender_group}
                      <span className="text-[10px] font-bold bg-brand-light text-brand px-1.5 py-0.5 rounded-full uppercase">{c.level || "brand"}</span>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase ${badge}`} data-testid={`chart-status-${c.id}`}>{st.replace("_", " ")}</span>
                      <span className="text-ink-muted font-normal">v{c.version}{c.active ? "" : " (old)"}</span>
                    </p>
                    <p className="text-xs text-ink-muted truncate">{c.product_name ? `${c.product_name} · ` : ""}{c.style_sku ? `${c.style_sku} · ` : ""}{c.rows?.length || 0} rows · source: {c.source || "—"} · verified {c.date_verified || "—"} · by {c.entered_by || "—"}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {st !== "verified" && <button data-testid={`chart-verify-${c.id}`} onClick={() => setStatus(c.id, "verified")} title="Mark verified" className="iconbtn text-success"><ShieldCheck size={15} /></button>}
                    {st !== "needs_review" && <button data-testid={`chart-review-${c.id}`} onClick={() => setStatus(c.id, "needs_review")} title="Mark needs review" className="iconbtn text-gold-text"><AlertTriangle size={15} /></button>}
                    {st !== "retired" && <button data-testid={`chart-retire-${c.id}`} onClick={() => setStatus(c.id, "retired")} title="Retire (never deleted)" className="iconbtn text-ink-muted"><EyeOff size={15} /></button>}
                    <button data-testid={`chart-del-${c.id}`} onClick={async () => { const { data } = await api.delete(`/admin/size-charts/${c.id}`); toast.success(data.retired ? "Verified chart retired (kept for audit)." : "Chart removed."); load(); }} className="iconbtn text-brand"><Trash2 size={15} /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <style>{`.card{background:#fff;border:1px solid #F4E3E6;border-radius:1.5rem;padding:1.25rem;box-shadow:0 1px 2px rgba(0,0,0,.04)}.lbl{font-size:.7rem;font-weight:800;text-transform:uppercase;letter-spacing:.04em;color:#9A8A8D}.fld{margin-top:.25rem;width:100%;height:2.6rem;padding:0 .7rem;border-radius:.9rem;border:2px solid #F4E3E6;background:#fff;outline:none;font-size:.85rem}.fld:focus{border-color:#FF4D6D}`}</style>
    </div>
  );
}


export function AdminStyles() {
  return <style>{`
    .ainput{width:100%;height:2.75rem;padding:0 .8rem;border-radius:.9rem;border:2px solid #F4E3E6;background:#FFF9FA;outline:none;font-size:.9rem}
    .ainput:focus{border-color:#FF4D6D}
    .btn-primary{display:inline-flex;align-items:center;gap:.4rem;height:2.75rem;padding:0 1.2rem;border-radius:9999px;background:#FF4D6D;color:#fff;font-weight:700;font-size:.9rem;transition:all .2s}
    .btn-primary:hover{background:#E83A58}
    .btn-ghost{display:inline-flex;align-items:center;gap:.4rem;height:2.75rem;padding:0 1.2rem;border-radius:9999px;background:#fff;border:2px solid #F4E3E6;color:#2D2327;font-weight:700;font-size:.9rem;transition:all .2s}
    .btn-ghost:hover{border-color:rgba(255,77,109,.3)}
    .iconbtn{width:2rem;height:2rem;display:inline-flex;align-items:center;justify-content:center;border-radius:.6rem;background:#FFF9FA;transition:all .2s}
    .iconbtn:hover{background:#FFF0F3}
    table th{font-size:.7rem;text-transform:uppercase;letter-spacing:.05em;font-weight:700;padding:.75rem}
  `}</style>;
}


function SizingEngine() {
  const [data, setData] = useState({ rows: [], total: 0, approved: 0, showing: 0 });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/sizing/rows", { params: { q, status } });
    setData(data);
  }, [q, status]);
  useEffect(() => { load(); }, [load]);

  const setRowStatus = async (rid, s) => {
    try { await api.post(`/admin/sizing/rows/${rid}/status`, { status: s }); toast.success(`Marked ${s}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const reimport = async () => {
    if (!window.confirm("Re-import the Siblingo workbook? This replaces the imported reference tables with the workbook's current data.")) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/sizing/reimport"); toast.success(`Re-imported ${data.rows} rows`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/sizing/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = "siblingo_sizing_rows.csv"; a.click();
      URL.revokeObjectURL(url); toast.success("Exported sizing rows.");
    } catch { toast.error("Export failed."); }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-gold/10 border border-gold/30 p-4 text-sm text-ink">
        <b>Sizing Intelligence foundation.</b> Imported from the Siblingo workbook. Only <b>Approved</b> rows drive recommendations. The engine matches Brand + Department + Clothing Type + Fit + Size and uses the Standard Scale + Brand Conversion Index for cross-brand mapping — never inventing measurements. Rows stay fully editable here so verified updates don't require a rebuild.
      </div>

      <div className="card">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <h3 className="font-heading font-bold text-lg text-ink">Sizing rows — {data.total} total · <span className="text-success">{data.approved} approved</span></h3>
          <div className="flex items-center gap-2 flex-wrap">
            <input data-testid="sizing-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search brand/type/size…" className="h-9 px-3 rounded-lg border-2 border-line bg-white text-sm w-56" />
            {["", "Approved", "Review required", "Retired"].map((s) => (
              <button key={s || "all"} data-testid={`sizing-filter-${s || "all"}`} onClick={() => setStatus(s)} className={`h-9 px-3 rounded-full text-xs font-bold transition-all ${status === s ? "bg-brand text-white" : "bg-cream text-ink-soft border border-line"}`}>{s || "All"}</button>
            ))}
            <button data-testid="sizing-export-btn" onClick={exportCsv} className="btn-ghost"><FileText size={15} /> Export</button>
            <button data-testid="sizing-reimport-btn" onClick={reimport} disabled={busy} className="btn-ghost disabled:opacity-60"><RefreshCw size={15} /> {busy ? "Importing…" : "Re-import"}</button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-ink-muted"><th>Record</th><th>Brand</th><th>Dept</th><th>Type</th><th>Fit</th><th>Size</th><th>Band</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {data.rows.map((r) => {
                const st = r.status || "Review required";
                const badge = st === "Approved" ? "bg-success/10 text-success" : st === "Retired" ? "bg-ink/10 text-ink-muted" : "bg-gold/15 text-gold-text";
                return (
                  <tr key={r.id} data-testid={`sizing-row-${r.record_id}`} className="border-t border-line">
                    <td className="py-2 text-xs text-ink-muted">{r.record_id}</td>
                    <td className="font-semibold text-ink">{r.brand}</td>
                    <td>{r.department}</td><td>{r.clothing_type}</td><td>{r.fit}</td>
                    <td className="font-bold">{r.retailer_size}</td>
                    <td className="text-xs text-ink-muted">{r.std_band}</td>
                    <td><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${badge}`}>{st}</span></td>
                    <td className="text-right whitespace-nowrap">
                      {st !== "Approved" && <button data-testid={`sizing-approve-${r.record_id}`} onClick={() => setRowStatus(r.id, "Approved")} title="Approve" className="iconbtn text-success"><ShieldCheck size={15} /></button>}
                      {st !== "Review required" && <button data-testid={`sizing-review-${r.record_id}`} onClick={() => setRowStatus(r.id, "Review required")} title="Send to review" className="iconbtn text-gold-text"><AlertTriangle size={15} /></button>}
                      {st !== "Retired" && <button data-testid={`sizing-retire-${r.record_id}`} onClick={() => setRowStatus(r.id, "Retired")} title="Retire" className="iconbtn text-ink-muted"><EyeOff size={15} /></button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {data.showing >= 500 && <p className="text-xs text-ink-muted mt-2">Showing first 500 — use search to narrow.</p>}
        </div>
      </div>
    </div>
  );
}


function PartnersManager() {
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState({ amazon_tag: "", temu_affiliate: "" });
  const [imgSettings, setImgSettings] = useState({ auto_approve_threshold: 0 });
  const [saveState, setSaveState] = useState({}); // id -> {status:'saving'|'saved'|'error', msg}
  const [pct, setPct] = useState({});             // id -> local commission % string (for smooth typing)
  const savedTimers = useRef({});
  const stateRef = useRef({});   // id -> authoritative {affiliate, commission_pct, auto_publish, rev}
  const chains = useRef({});     // id -> promise chain that serializes writes per row
  const [pending, setPending] = useState({ pending: [], pending_count: 0 });
  const [q, setQ] = useState("");
  const [affOnly, setAffOnly] = useState(false);

  const seedPct = (list) => {
    const p = {};
    const m = {};
    list.forEach((x) => {
      p[x.id] = String(x.commission_pct ?? 0);
      m[x.id] = { affiliate: !!x.affiliate, commission_pct: x.commission_pct ?? 0, auto_publish: !!x.auto_publish, may_post_deals: !!x.may_post_deals, rev: x.rev ?? 0 };
    });
    setPct(p);
    stateRef.current = m;
  };

  const load = useCallback(async () => {
    const [u, s, p, im] = await Promise.all([
      api.get("/admin/users"), api.get("/admin/affiliate/settings"),
      api.get("/admin/affiliate/pending"), api.get("/admin/images/settings"),
    ]);
    setUsers(u.data);
    setSettings(s.data);
    setPending(p.data);
    setImgSettings(im.data);
    seedPct(u.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const patchUser = (id, fields) => setUsers((list) => list.map((u) => (u.id === id ? { ...u, ...fields } : u)));
  // Update both the visible row and the authoritative ref from a server view.
  const applyServer = (id, view) => {
    stateRef.current[id] = {
      affiliate: !!view.affiliate, commission_pct: view.commission_pct ?? 0,
      auto_publish: !!view.auto_publish, may_post_deals: !!view.may_post_deals, rev: view.rev ?? 0,
    };
    patchUser(id, view);
  };
  const setSave = (id, s) => setSaveState((p) => ({ ...p, [id]: s }));
  const clearSave = (id) => setSaveState((p) => { const n = { ...p }; delete n[id]; return n; });
  const flashSaved = (id) => {
    setSave(id, { status: "saved" });
    clearTimeout(savedTimers.current[id]);
    savedTimers.current[id] = setTimeout(() => {
      setSaveState((p) => { if (p[id]?.status !== "saved") return p; const n = { ...p }; delete n[id]; return n; });
    }, 2000);
  };

  const saveSettings = async () => {
    try { await api.put("/admin/affiliate/settings", settings); toast.success("Affiliate tags saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const saveImgSettings = async () => {
    try { const { data } = await api.post("/admin/images/settings", imgSettings); setImgSettings(data); toast.success("Image review rule saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  // Autosave affiliate fields together, optimistically. Writes are SERIALIZED per row
  // (chained promise) so a rapid second edit always uses the freshest rev from the server.
  const saveAffiliate = (id, changes) => {
    const run = async () => {
      const cur = stateRef.current[id];
      if (!cur) return;
      const prev = { ...cur };
      const next = { ...cur, ...changes };
      // optimistic paint (keep rev until the server confirms the new one)
      stateRef.current[id] = { ...next, rev: cur.rev };
      patchUser(id, { affiliate: next.affiliate, commission_pct: next.commission_pct, auto_publish: next.auto_publish, may_post_deals: next.may_post_deals });
      setSave(id, { status: "saving" });
      try {
        const { data } = await api.put(`/admin/users/${id}/affiliate`, {
          affiliate: !!next.affiliate,
          commission_pct: Number(next.commission_pct) || 0,
          auto_publish: !!next.auto_publish,
          may_post_deals: !!next.may_post_deals,
          expected_rev: cur.rev,
        });
        applyServer(id, data);
        setPct((p) => ({ ...p, [id]: String(data.commission_pct) }));
        flashSaved(id);
      } catch (e) {
        const conflict = e.response?.status === 409 && e.response.data?.detail?.latest;
        if (conflict) {
          const latest = e.response.data.detail.latest;
          applyServer(id, latest);
          setPct((p) => ({ ...p, [id]: String(latest.commission_pct) }));
          setSave(id, { status: "error", msg: e.response.data.detail.msg });
        } else {
          stateRef.current[id] = prev; // restore previous value on failure
          patchUser(id, { affiliate: prev.affiliate, commission_pct: prev.commission_pct, auto_publish: prev.auto_publish, may_post_deals: prev.may_post_deals });
          setPct((p) => ({ ...p, [id]: String(prev.commission_pct) }));
          setSave(id, { status: "error", msg: apiError(e.response?.data?.detail) });
        }
      }
    };
    const nextChain = (chains.current[id] || Promise.resolve()).then(run, run);
    chains.current[id] = nextChain;
    return nextChain;
  };

  const commitPct = (id) => {
    const cur = stateRef.current[id];
    if (!cur) return;
    let v = Number(pct[id]);
    if (Number.isNaN(v)) v = cur.commission_pct ?? 0;
    v = Math.max(0, Math.min(100, v));
    setPct((p) => ({ ...p, [id]: String(v) }));
    if (v !== cur.commission_pct) saveAffiliate(id, { commission_pct: v });
  };

  // Access writes are also serialized per row so they don't collide with affiliate writes.
  const runAccess = (id, body, onOk) => {
    const run = async () => {
      const cur = stateRef.current[id];
      setSave(id, { status: "saving" });
      try {
        const { data } = await api.put(`/admin/users/${id}/access`, { ...body, expected_rev: cur?.rev });
        applyServer(id, data);
        onOk(data);
        flashSaved(id);
      } catch (e) {
        if (e.response?.status === 409 && e.response.data?.detail?.latest) {
          applyServer(id, e.response.data.detail.latest);
          setSave(id, { status: "error", msg: e.response.data.detail.msg });
        } else { setSave(id, { status: "error", msg: apiError(e.response?.data?.detail) }); }
        toast.error(apiError(e.response?.data?.detail));
      }
    };
    const nextChain = (chains.current[id] || Promise.resolve()).then(run, run);
    chains.current[id] = nextChain;
    return nextChain;
  };

  const extendTrial = (id, days) =>
    runAccess(id, { extend_days: days }, (data) => toast.success(`Trial extended · ${data.trial_days_remaining}d left`));
  const toggleComp = (id, current) =>
    runAccess(id, { free_access: !current }, (data) => toast.success(data.free_access ? "Complimentary access granted" : "Complimentary access revoked"));

  const setD = null; void setD;
  const shown = users.filter((u) =>
    (!affOnly || u.affiliate) &&
    (!q || `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="space-y-6" data-testid="partners-manager">
      {/* Partner submissions — curated online products from active affiliate partners go to Listing Approvals */}
      <div className="bg-white rounded-3xl border border-line shadow-soft p-5" data-testid="partner-submissions-panel">
        <h3 className="font-heading font-bold text-lg text-ink mb-1">Partner submissions ({pending.pending_count ?? 0} awaiting review)</h3>
        <p className="text-sm text-ink-muted mb-3">Curated online products posted by active affiliate partners enter the same <b>Listing Approvals</b> queue as manual, CSV, feed and AI-assisted listings (source “Affiliate partner”). Nothing a partner posts publishes without your Approve click.{pending.sales_division_enabled ? ` Legacy sales-based partner deals: ${pending.legacy_sales_pending} pending (sales division).` : " Legacy sales-based partner deals are not counted while the sales division is disabled."}</p>
        <div className="flex flex-wrap gap-2 items-center">
          <a href="/admin?tab=approvals&view=all&source=affiliate_partner" data-testid="partner-submissions-open-queue" className="btn-primary h-10 text-sm">Review in Listing Approvals</a>
          <span className="text-xs text-ink-muted" data-testid="partner-submissions-total">{pending.total_partner_products ?? 0} partner product{(pending.total_partner_products ?? 0) === 1 ? "" : "s"} total</span>
        </div>
        {(pending.pending || []).length > 0 && (
          <ul className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="pending-affiliate-list">
            {pending.pending.map((s) => (
              <li key={s.id} data-testid={`pending-affiliate-${s.id}`} className="rounded-xl border border-line p-3 text-xs space-y-1">
                <p className="font-bold text-ink text-sm leading-snug">{s.name}</p>
                <p className="text-ink-muted">{s.retailer} · {s.contributor}{s.price ? ` · $${s.price}` : ""}</p>
                <p className={`font-bold ${s.review_state === "blocked" ? "text-red-700" : "text-amber-800"}`}>{s.review_state === "blocked" ? `Blocked (${s.blockers.length} reason${s.blockers.length === 1 ? "" : "s"})` : s.review_state === "needs_changes" ? "Needs changes" : "Pending review"}</p>
                <a href={`/admin?tab=approvals&view=all&source=affiliate_partner&focus=${s.id}`} className="text-brand underline" data-testid={`pending-affiliate-open-${s.id}`}>Open card</a>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-white rounded-3xl border border-line shadow-soft p-5 space-y-3">
        <h3 className="font-heading font-bold text-lg text-ink">Your affiliate tags</h3>
        <p className="text-sm text-ink-muted">Approved deals get rewritten to include these. Paste them whenever ready.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-ink-muted">Amazon Associates tag</label>
            <input data-testid="affiliate-amazon-tag" value={settings.amazon_tag} onChange={(e) => setSettings({ ...settings, amazon_tag: e.target.value })} placeholder="e.g. uptoo-20" className="mt-1 w-full h-11 px-3 rounded-xl border-2 border-line bg-white focus:border-brand outline-none" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-ink-muted">Temu affiliate link/ID</label>
            <input data-testid="affiliate-temu" value={settings.temu_affiliate} onChange={(e) => setSettings({ ...settings, temu_affiliate: e.target.value })} placeholder="ID, or template with {url}" className="mt-1 w-full h-11 px-3 rounded-xl border-2 border-line bg-white focus:border-brand outline-none" />
          </div>
        </div>
        <button onClick={saveSettings} data-testid="affiliate-settings-save" className="btn-primary"><Check size={16} /> Save tags</button>
      </div>

      <div className="bg-white rounded-3xl border border-line shadow-soft p-5 space-y-3" data-testid="image-review-settings">
        <h3 className="font-heading font-bold text-lg text-ink flex items-center gap-2"><ImageIcon size={18} className="text-brand" /> Image modesty auto-approve rule</h3>
        <p className="text-sm text-ink-muted">Product images containing a person are held for review. Set a modesty threshold to auto-approve images scoring at or above it — this reduces manual review over time. Set to <strong>Off</strong> to review every flagged image yourself.</p>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-xs font-bold uppercase tracking-wider text-ink-muted">Auto-approve at score ≥</label>
          <select data-testid="modesty-threshold-select" value={imgSettings.auto_approve_threshold}
            onChange={(e) => setImgSettings({ auto_approve_threshold: Number(e.target.value) })}
            className="h-11 px-3 rounded-xl border-2 border-line bg-white focus:border-brand outline-none">
            <option value={0}>Off — review all</option>
            <option value={5}>5/10 and above</option>
            <option value={6}>6/10 and above</option>
            <option value={7}>7/10 and above</option>
            <option value={8}>8/10 and above</option>
            <option value={9}>9/10 and above</option>
            <option value={10}>10/10 only</option>
          </select>
          <button onClick={saveImgSettings} data-testid="modesty-threshold-save" className="btn-primary"><Check size={16} /> Save rule</button>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-line shadow-soft p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <h3 className="font-heading font-bold text-lg text-ink">Users &amp; affiliate partners</h3>
          <div className="flex items-center gap-2">
            <input data-testid="partner-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name/email…" className="h-9 px-3 rounded-lg border-2 border-line bg-white text-sm w-52" />
            <button data-testid="partner-affonly-toggle" onClick={() => setAffOnly((v) => !v)} className={`h-9 px-3 rounded-full text-xs font-bold transition-all ${affOnly ? "bg-brand text-white" : "bg-cream text-ink-soft border border-line"}`}>Affiliates only</button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-ink-muted"><th>Name / Email</th><th>Plan</th><th>Access</th><th>Affiliate Partner</th><th>May Post Deals</th><th>Commission %</th><th>Auto-publish</th><th className="text-right sticky right-0 bg-white">Status</th></tr></thead>
            <tbody>
              {shown.slice(0, 100).map((u) => {
                const planBadge = u.plan_status === "premium" ? "bg-gold/15 text-gold-text"
                  : u.plan_status === "comp" ? "bg-brand-light text-brand"
                  : u.plan_status === "trial" ? "bg-success/10 text-success"
                  : u.plan_status === "trial_expired" ? "bg-ink/10 text-ink-muted" : "bg-cream text-ink-muted";
                const planText = u.plan_status === "trial" ? `Trial · ${u.trial_days_remaining}d`
                  : u.plan_status === "comp" ? "Complimentary"
                  : u.plan_status === "trial_expired" ? "Trial ended"
                  : u.plan_status === "premium" ? "Premium" : "Free";
                const st = saveState[u.id] || {};
                const cpct = pct[u.id] ?? String(u.commission_pct ?? 0);
                return (
                <tr key={u.id} data-testid={`partner-row-${u.id}`} className="border-t border-line">
                  <td className="py-3">
                    <p className="font-bold text-ink">{u.name || "—"}</p>
                    <p className="text-xs text-ink-muted">{u.email}{u.role === "admin" ? " · admin" : ""}</p>
                  </td>
                  <td>
                    <span data-testid={`user-plan-${u.id}`} className={`text-[11px] font-bold px-2 py-1 rounded-full whitespace-nowrap ${planBadge}`}>{planText}</span>
                  </td>
                  <td>
                    <div className="flex items-center gap-1.5">
                      <button data-testid={`user-extend-${u.id}`} onClick={() => extendTrial(u.id, 30)} aria-label="Add 30 days to trial" title="Add 30 days to this user's trial" className="h-8 px-2.5 rounded-full bg-cream border border-line text-xs font-bold text-ink-soft hover:bg-brand-light hover:text-brand transition-all whitespace-nowrap">Add 30 days</button>
                      <button data-testid={`user-comp-${u.id}`} onClick={() => toggleComp(u.id, u.free_access)} aria-label={u.free_access ? "Revoke complimentary access" : "Grant complimentary access"} title="Grant or revoke complimentary (indefinite, no-charge) full access for this user" className={`h-8 px-2.5 rounded-full text-xs font-bold transition-all whitespace-nowrap ${u.free_access ? "bg-brand text-white" : "bg-cream border border-line text-ink-soft hover:bg-brand-light hover:text-brand"}`}>{u.free_access ? "Complimentary ✓" : "Complimentary"}</button>
                    </div>
                  </td>
                  <td>
                    <button data-testid={`partner-toggle-${u.id}`} role="switch" aria-checked={!!u.affiliate} aria-label="Affiliate Partner" onClick={() => saveAffiliate(u.id, { affiliate: !u.affiliate })}
                      className={`relative w-12 h-7 rounded-full transition-colors outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1 ${u.affiliate ? "bg-brand" : "bg-line"}`}>
                      <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${u.affiliate ? "translate-x-5" : ""}`} />
                    </button>
                  </td>
                  <td>
                    <button data-testid={`partner-postdeals-${u.id}`} role="switch" aria-checked={!!u.may_post_deals} aria-label="May Post Deals" title="Allow this partner to submit deals (independent of commission)" onClick={() => saveAffiliate(u.id, { may_post_deals: !u.may_post_deals })}
                      className={`relative w-12 h-7 rounded-full transition-colors outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1 ${u.may_post_deals ? "bg-brand" : "bg-line"}`}>
                      <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${u.may_post_deals ? "translate-x-5" : ""}`} />
                    </button>
                  </td>
                  <td>
                    <input data-testid={`partner-pct-${u.id}`} aria-label="Commission percent" type="number" min="0" max="100" value={cpct}
                      onChange={(e) => setPct((p) => ({ ...p, [u.id]: e.target.value }))}
                      onBlur={() => commitPct(u.id)}
                      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                      className="w-20 h-9 px-2 rounded-lg border-2 border-line bg-white focus:border-brand outline-none" />
                  </td>
                  <td>
                    <button data-testid={`partner-autopublish-${u.id}`} role="switch" aria-checked={!!u.auto_publish} aria-label="Auto-publish" title="Auto-publish clean images without manual review" onClick={() => saveAffiliate(u.id, { auto_publish: !u.auto_publish })}
                      className={`relative w-12 h-7 rounded-full transition-colors outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-1 ${u.auto_publish ? "bg-emerald-600" : "bg-line"}`}>
                      <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${u.auto_publish ? "translate-x-5" : ""}`} />
                    </button>
                  </td>
                  <td className="text-right sticky right-0 bg-white">
                    <div data-testid={`partner-savestate-${u.id}`} className="inline-flex items-center gap-1.5 min-h-[24px]">
                      {st.status === "saving" && <span className="inline-flex items-center gap-1 text-xs font-bold text-ink-muted"><RefreshCw size={12} className="animate-spin" /> Saving…</span>}
                      {st.status === "saved" && <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600"><Check size={13} /> Saved</span>}
                      {st.status === "error" && (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="inline-flex items-center gap-1 text-xs font-bold text-red-600" title={st.msg}><AlertTriangle size={12} /> {st.msg?.includes("elsewhere") ? "Reloaded" : "Not saved"}</span>
                          <button data-testid={`partner-dismiss-${u.id}`} onClick={() => clearSave(u.id)} className="text-[11px] text-ink-muted underline">dismiss</button>
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          {shown.length > 100 && <p className="text-xs text-ink-muted mt-2">Showing first 100 of {shown.length}. Use search to narrow.</p>}
        </div>
      </div>
    </div>
  );
}

function EarningsReport() {
  const [data, setData] = useState({ partners: [], total_owed: 0 });
  const [draft, setDraft] = useState({});

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/affiliate/earnings");
    setData(data);
    const d = {};
    data.partners.forEach((p) => { d[p.id] = { affiliate_revenue: p.affiliate_revenue, affiliate_paid: p.affiliate_paid }; });
    setDraft(d);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (id) => {
    try { await api.put(`/admin/affiliate/earnings/${id}`, draft[id]); toast.success("Saved"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const setD = (id, k, v) => setDraft((p) => ({ ...p, [id]: { ...p[id], [k]: v } }));

  return (
    <div className="space-y-4" data-testid="earnings-report">
      <div className="bg-white rounded-3xl border border-line shadow-soft p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-bold text-lg text-ink">Affiliate earnings</h3>
          <span className="text-sm font-bold text-ink">Total owed: <span className="text-brand">${data.total_owed.toFixed(2)}</span></span>
        </div>
        <p className="text-xs text-ink-muted mb-3">Clicks and deals are tracked automatically. Enter the affiliate revenue from your network dashboard; the app computes each partner's share by their %.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-ink-muted"><th>Partner</th><th>Deals</th><th>Clicks</th><th>%</th><th>Revenue ($)</th><th>Earned</th><th>Paid ($)</th><th>Owed</th><th></th></tr></thead>
            <tbody>
              {data.partners.length === 0 && <tr><td colSpan="9" className="py-6 text-center text-ink-muted">No affiliate partners yet. Enable partners in the Partners tab.</td></tr>}
              {data.partners.map((p) => (
                <tr key={p.id} data-testid={`earning-row-${p.id}`} className="border-t border-line">
                  <td className="py-3"><p className="font-bold text-ink">{p.name}</p><p className="text-xs text-ink-muted">{p.email}</p></td>
                  <td data-testid={`earning-deals-${p.id}`}>{p.deals}</td>
                  <td data-testid={`earning-clicks-${p.id}`}>{p.clicks}</td>
                  <td data-testid={`earning-pct-${p.id}`}>{p.commission_pct}%</td>
                  <td><input data-testid={`earning-revenue-${p.id}`} type="number" value={draft[p.id]?.affiliate_revenue ?? 0} onChange={(e) => setD(p.id, "affiliate_revenue", Number(e.target.value))} className="w-24 h-9 px-2 rounded-lg border-2 border-line bg-white" /></td>
                  <td data-testid={`earning-earned-${p.id}`} className="font-bold text-ink">${p.commission_earned.toFixed(2)}</td>
                  <td><input data-testid={`earning-paid-${p.id}`} type="number" value={draft[p.id]?.affiliate_paid ?? 0} onChange={(e) => setD(p.id, "affiliate_paid", Number(e.target.value))} className="w-24 h-9 px-2 rounded-lg border-2 border-line bg-white" /></td>
                  <td data-testid={`earning-owed-${p.id}`} className="font-bold text-brand">${p.owed.toFixed(2)}</td>
                  <td className="text-right"><button data-testid={`earning-save-${p.id}`} onClick={() => save(p.id)} className="h-9 px-3 rounded-full bg-ink text-white text-xs font-bold hover:bg-ink-soft transition-all">Save</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
